// Экран «Модель ГПР» (таблица задач) — шестой экран нового фронта
// (п.20, 2026-09-07). Встраивается в блок таблицы: шапка «Модели» (виды, столбцы,
// «Актуализировать», базовый план) остаётся за оболочкой; Гант и Сводная — тоже.
// Что здесь: группировка этап → работа → задачи, сортировка кликом по заголовку,
// правка ячеек, мультивыбор с пакетной правкой, окна «как получена дата»,
// длительности и предшественников, строка добавления ручной задачи внизу,
// виртуализация строк (в проекте до 10 тыс. задач).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { call, p, type Data } from '../api';
import { PoleSpiska } from '../Podskazki';
import { TaskCard } from '../TaskCard';
import { ShahmatkaGPR } from '../ShahmatkaGPR';
import { GantHolst, PX_MAX, PX_MIN, type HPolosa } from '../GantHolst';
import { Pop, at, type Anchor } from '../Pop';
import { ask, confirmBox } from '../dialog';
import type { ShellCtx } from '../shell';
import { fmt, kontury, parseDate, tipColor } from '../util';

type Tasks = Data<'/api/projects/{pid}/tasks', 'get'>;
type Row = Tasks['rows'][number];
type Krit = Data<'/api/projects/{pid}/kriticheskij-put', 'get'>;
type Work = Data<'/api/projects/{pid}/works', 'get'>[number];
type Floor = Data<'/api/projects/{pid}/structure', 'get'>[string][number];
type Wbs = Data<'/api/projects/{pid}/wbs', 'get'>;
// ручное планирование (#126, 2026-10-27): группа дерева, строка дерева и место строки ввода
type RG = Data<'/api/projects/{pid}/ruch-gruppy', 'get'>[number];
type Uzel = { tip: 'task' | 'gruppa'; id: number };
type Vvod = { parent: number | null; posle: Uzel | 'nachalo' | null };
// связь с группой ручного планирования (#127): «задача → задача» — обычная ручная, сюда не попадает
type RSv = Data<'/api/projects/{pid}/ruch/svyazi', 'get'>[number];
import { useIerarhiya, IerImya, IerPolya, IerKontur, IerPodskazka, estDeti as ierEstDeti, konecPoddereva as ierKonec } from './Ierarhiya';
import { SvyaziRaboty } from '../SvyaziRaboty';
import { RASK_LABEL } from './Works';
import { SvyaziShema, urovni as svUrovni, SV_SHAG, SV_SHIRINA, SV_OTSTUP } from '../SvyaziShema';
import type { GantKnopki } from '../GantHolst';
type SvodRow = Data<'/api/link-templates/rows', 'get'>[number];
type Explain = Data<'/api/tasks/{tid}/explain', 'get'>;
type LinksGet = Data<'/api/tasks/{tid}/links', 'get'>;

const D1 = 86400000;
const dnum = (s?: string | null) => (s ? Math.round(new Date(s + 'T00:00:00Z').getTime() / D1) : null);
const NO_ETAP = '— без этапа —';
const VNE_IER = '— вне иерархии —';   // откройте «Иерархию» — работа встанет в конец структуры
const ROW_H = 25;          // высота строки таблицы, нужна для виртуализации
const MES = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const MW = 54;             // ширина месяца в правой части (Гант и сводная)
const WINDOW_PAD = 12;     // строк запаса сверху и снизу окна прокрутки
// нижняя панель деталей задачи (2026-09-14): пределы высоты и её память
const DET_MIN = 360, DET_MAX = 1000, DET_KEY = 'plot_detali_w';   // ширина правой панели (2026-10-18; была высота нижней)
// Гант на холсте (прототип 2026-09-18): масштаб в пикселях на день и его память
const PX_KEY = 'plot_gant_px';
function loadPx(): number {
  try {
    const n = Number(localStorage.getItem(PX_KEY));
    return n >= PX_MIN && n <= PX_MAX ? n : 3;
  } catch { return 3; }
}
function loadDetH(): number {
  try {
    const n = Number(localStorage.getItem(DET_KEY));
    return n >= DET_MIN && n <= DET_MAX ? n : 460;
  } catch { return 300; }          // приватный режим браузера
}

type Col = {
  key: string; cls?: string; grp?: string; date?: boolean;
  get: (r: Row) => string; manual: (r: Row) => boolean;
  options?: (podr: string[]) => string[];
  apply: (r: Row, v: string | null, d?: Record<string, unknown>) => void;
};
const predRuchText = (r: Row) => (r.mp || []).map(m =>
  m.nomer + (m.tip === 'НН' ? 'НН' : '') + (m.zapas ? (m.zapas > 0 ? '+' : '') + m.zapas : '') + (m.soft ? 'м' : '')).join('; ');
const num = (v: string | null) => (v ? +String(v).replace(',', '.') : null);

// колонки блока «Ручной ввод» в порядке таблицы (плюс подрядчик в блоке «Факт»)
// Колонки группы «Корректировка» (2026-09-08): исключение, объём и ручные
// предшественники переехали в карточку задачи, здесь остались правки расчёта
// «Начало» и «Окончание» блока «Расчёт» правятся прямо в ячейке (владелец 2026-10-18: «пользователь ставит дату
// начала и окончания вручную, она выделена цветом, от неё пересчёт»): показывают итоговую дату, ввод пишет ручную
// (те же поля, что «Начало»/«Оконч.» в «Сроках»), пусто — снять ручную
const NACH_ITOG: Col = { key: 'nachalo_vruchnuyu', date: true, get: r => fmt(r.nach_vr ?? r.nachalo), manual: r => r.nach_vr != null,
  apply: (r, v) => { r.nach_vr = v; } };
const OKONCH_ITOG: Col = { key: 'okonchanie_vruchnuyu', date: true, get: r => fmt(r.okonch_vr ?? r.okonchanie), manual: r => r.okonch_vr != null,
  apply: (r, v) => { r.okonch_vr = v; } };
// длительность в «Расчёте» — правка прямо в ячейке, как даты (владелец 2026-10-27: «не нужно окно»): показывается
// итоговая, пишется ручная; пусто — снова автоматическая
const DLIT_ITOG: Col = { key: 'dlit_ruchnaya', get: r => String(r.dlit ?? ''), manual: r => r.dlit_r != null,
  apply: (r, v) => { r.dlit_r = num(v); r.dd = null; r.dlit = r.dlit_r != null ? r.dlit_r : r.dlit_avto; } };
// ручное планирование (2026-10-27): корпус, этаж и место ручной задачи — в ячейке, как в Excel
const KORPUS_RUCH: Col = { key: 'korpus', get: r => r.korpus || 'Объект', manual: () => true, options: k => k,
  apply: (r, v) => { r.korpus = v || 'Объект'; } };
const ETAZH_RUCH: Col = { key: 'floor_label', get: r => r.etazh ?? '', manual: r => !!r.etazh, apply: (r, v) => { r.etazh = v; } };
const MESTO_RUCH: Col = { key: 'zahvatka', get: r => r.zahvatka ?? '', manual: r => !!r.zahvatka, apply: (r, v) => { r.zahvatka = v; } };

const HAND: Col[] = [
  { key: 'dlit_ruchnaya', get: r => String(r.dlit_r ?? ''), manual: r => r.dlit_r != null,
    apply: (r, v) => { r.dlit_r = num(v); r.dd = null; r.dlit = r.dlit_r != null ? r.dlit_r : r.dlit_avto; } },
  // декоративная длительность (#53, 2026-09-13): растягивание суммарной на Ганте
  { key: 'dlit_dekor', get: r => String(r.dd ?? ''), manual: r => r.dd != null,
    apply: (r, v) => { r.dd = num(v); r.dlit = r.dd != null ? r.dd : (r.dlit_r != null ? r.dlit_r : r.dlit_avto); } },
  { key: 'zapas', get: r => String(r.zapas ?? ''), manual: r => r.zapas != null, apply: (r, v) => { r.zapas = num(v); } },
  // рамка планера «не раньше»: ставится сдвигом суммарной полосы, снимается сама
  // при факте и ручной правке (2026-09-09)
  { key: 'ogranichenie', date: true, get: r => fmt(r.ogr), manual: r => r.ogr != null,
    apply: (r, v) => { r.ogr = v; } },
  { key: 'nachalo_vruchnuyu', date: true, get: r => fmt(r.nach_vr), manual: r => r.nach_vr != null,
    apply: (r, v) => { r.nach_vr = v; } },
  { key: 'okonchanie_vruchnuyu', date: true, get: r => fmt(r.okonch_vr), manual: r => r.okonch_vr != null,
    apply: (r, v) => { r.okonch_vr = v; } },
];
// группа «Ресурс» (2026-09-08): объём и ручной приоритет в очереди бригады
const RES: Col[] = [
  { key: 'obyom', cls: 'col-res', get: r => String(r.obyom ?? ''), manual: r => r.obyom != null,
    apply: (r, v) => { r.obyom = num(v); } },
  { key: 'prioritet_vruchnuyu', cls: 'col-res', get: r => String(r.prior ?? ''), manual: r => r.prior != null,
    apply: (r, v) => { r.prior = num(v); } },
  { key: 'nomer_brigady', cls: 'col-res', get: r => String(r.brigada ?? ''), manual: r => r.brigada != null,
    apply: (r, v) => { r.brigada = num(v); } },
  { key: 'podryadchik', cls: 'col-res', get: r => r.podryadchik ?? '', manual: () => false,
    options: podr => ['', ...podr], apply: (r, v) => { r.podryadchik = v; } },
];

// уровни сортировки: первый в списке — главный, следующие сортируют внутри него
type Sort = { i: number; dir: 1 | -1 }[];
type Display = { kind: 'etap'; name: string; key: string; glubina: number; min?: string; max?: string }
  | { kind: 'work'; name: string; min?: string; max?: string; open: boolean; vStroke?: boolean }
  // строка ГПР (#120, 2026-10-27): суммарная позиция над работами строки; работы — внутри
  | { kind: 'stroka'; name: string; key: string; min?: string; max?: string; open: boolean; rabot: number }
  | { kind: 'task'; row: Row; glubina?: number }
  // ручное планирование (#126): группа дерева («Котлован», «Захватка 1») и строка ввода на своём месте
  | { kind: 'rgr'; id: number; name: string; glubina: number; min?: string; max?: string; open: boolean; n: number }
  | { kind: 'vvod'; glubina: number }
  // режим «Иерархия» (2026-10-06): строка структуры — раздел или работа, правится на месте
  | { kind: 'ier'; i: number; name: string; key: string; razdel: boolean; min?: string; max?: string };

export function Plan({ ctx, vid = 'table', ruch = false, summ = false, raschet = false,
  svyazi = false, oshibki = false, vidOshibki = '', onOshibki, holst = false, ierarh = false, onGant, onIerarh, cep = ['etap', 'rabota'], uroven }:
  { ctx: ShellCtx; vid?: 'table' | 'gant' | 'pivot' | 'shahm'; ruch?: boolean; summ?: boolean;
    raschet?: boolean; svyazi?: boolean;
    /** включить режим «Иерархия» из подсказки пустого проекта (2026-10-15) */
    onIerarh?: () => void;
    /** режим «Иерархия»: та же таблица, свёрнутая до разделов и работ, строки правятся
        (владелец 2026-10-06: «один экран, два режима — строю и смотрю») */
    ierarh?: boolean;
    /** группировка (конструктор, 2026-10-17): уровни сверху вниз — 'etap' (основная разбивка), 'rz:<id>' (своя),
        'rabota', 'korpus', признаки работы ('sistema' | 'mesto' | 'kontur' | 'raskatka' | 'logika' | 'kalendar'),
        'podr'; пусто — без группировки. Работа последним уровнем — прежняя строка работы со свёрткой */
    cep?: string[];
    /** «Уровень» в третьей строке: раскрыть до уровня n (номер в cep; cep.length — до задач), t — метка нажатия */
    uroven?: { n: number; t: number };
    /** кнопки холста Ганта — наружу, в шапку модели */
    onGant?: (k: GantKnopki | null) => void;
    /** Гант на холсте (прототип 2026-09-18, переключатель «Новый Гант» у администратора) */
    holst?: boolean;
    /** кнопка «Ошибки» (владелец 2026-09-16): только задачи с замечаниями «ошибка»/«конфликт» */
    oshibki?: boolean;
    /** один вид замечания (название проверки); пусто — все */
    vidOshibki?: string;
    /** задачи с замечаниями среди отобранных панелью фильтров — число и разбивка по видам */
    onOshibki?: (s: SvodkaOshibok) => void }) {
  const [data, setData] = useState<Tasks | null>(null);
  const [kritOn, setKritOn] = useState(false);
  const [tolkoKrit, setTolkoKrit] = useState(false);
  const [kritData, setKritData] = useState<{ pid: number; value: Krit } | null>(null);
  const [kritError, setKritError] = useState('');
  useEffect(() => {
    setKritData(null); setKritError('');
    if (!kritOn || !data || raschet || ierarh || ctx.datyUstareli) return;
    let alive = true;
    void call<Krit>('get', p('/api/projects/{pid}/kriticheskij-put', { pid: ctx.pid })).then(r => {
      if (!alive) return;
      if (r.ok) setKritData({ pid: ctx.pid, value: r.data });
      else setKritError(r.error.message);
    });
    return () => { alive = false; };
  }, [kritOn, ctx.pid, data, raschet, ierarh, ctx.datyUstareli]);
  const kritColumn = kritOn && !raschet && !ierarh;
  const kritResult = kritData?.pid === ctx.pid ? kritData.value : null;
  const kritMap = useMemo(() => {
    const m = new Map<number, Krit['zadachi'][number]>();
    if (!kritColumn || ctx.datyUstareli || kritResult?.status !== 'ok' || !data) return m;
    const calculated = new Map(kritResult.zadachi.map(r => [r.task_id, r]));
    if (data.rows.some(r => !r.iskl && (!calculated.has(r.id) || calculated.get(r.id)!.rev !== r.rev
      || calculated.get(r.id)!.nachalo !== r.nachalo || calculated.get(r.id)!.okonchanie !== r.okonchanie))) return m;
    return calculated;
  }, [kritColumn, kritResult, data, ctx.datyUstareli]);
  const critical = (r: Row) => !!kritMap.get(r.id)?.krit;
  // задачи с замечаниями (ошибка/конфликт) — подсветка строк и кнопка «Ошибки»
  const [problemy, setProblemy] = useState<Map<number, Problema>>(new Map());
  const [rows, setRows] = useState<Row[]>([]);
  const [meta, setMeta] = useState<Record<string, Work>>({});
  const [struct, setStruct] = useState<Record<string, Floor[]>>({});
  const [wbs, setWbs] = useState<Wbs['uzly']>([]);   // иерархия работ (2026-10-06)
  // свои разбивки из группировки: id → узлы (2026-10-17)
  const [wbsRz, setWbsRz] = useState<Map<number, Wbs['uzly']>>(new Map());
  const cepKl = cep.join('|');
  useEffect(() => {
    const ids = cep.filter(k => k.startsWith('rz:')).map(k => +k.slice(3));
    if (!ids.length) { setWbsRz(new Map()); return; }
    let zhiv = true;
    void Promise.all(ids.map(id => call<Wbs>('get', p('/api/projects/{pid}/wbs', { pid: ctx.pid }) + '?razbivka=' + id)))
      .then(rs => { if (zhiv) setWbsRz(new Map(ids.map((id, i) => [id, rs[i].ok ? rs[i].data.uzly : []]))); });
    return () => { zhiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cepKl, ctx.pid, ctx.reloadToken]);
  const [svod, setSvod] = useState<SvodRow[]>([]);   // связи проекта — счётчик в столбце «Связи» режима иерархии
  const box = useRef<HTMLDivElement>(null);
  const ie = useIerarhiya(ctx, box, ierarh);
  const [podr, setPodr] = useState<string[]>([]);
  const [sort, setSort] = useState<Sort>([]);
  const [openWorks, setOpenWorks] = useState<Set<string>>(new Set());
  const [openStroki, setOpenStroki] = useState<Set<string>>(new Set());   // раскрытые строки ГПР (#120)
  // «Ошибки»: работы раскрываются, чтобы подсвеченные строки задач были видны сразу;
  // при выключении возвращается прежнее свёрнутое состояние
  const doOshibok = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (oshibki) {
      if (!doOshibok.current) doOshibok.current = openWorks;
      setOpenWorks(new Set((data?.rows || []).map(r => r.rabota)));
    } else if (doOshibok.current) {
      setOpenWorks(doOshibok.current);
      doOshibok.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oshibki, data]);
  const [closedEtap, setClosedEtap] = useState<Set<string>>(new Set());
  const [sel, setSel] = useState<Set<number>>(new Set());
  // ручное планирование (#126): группы, свёрнутые группы, текущая строка (для клавиш) и место строки ввода
  const [rGruppy, setRGruppy] = useState<RG[]>([]);
  const [rSvyazi, setRSvyazi] = useState<RSv[]>([]);
  const [rSvern, setRSvern] = useState<Set<number>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('plot_ruch_svern') || '[]') as number[]); } catch { return new Set(); }
  });
  useEffect(() => { try { localStorage.setItem('plot_ruch_svern', JSON.stringify([...rSvern])); } catch { /* приватный режим */ } }, [rSvern]);
  const [tek, setTek] = useState<Uzel | null>(null);
  const [vvod, setVvodS] = useState<Vvod>({ parent: null, posle: null });
  const [fokus, setFokus] = useState(0);   // меняется — строка ввода забирает курсор
  /** поставить строку ввода (её группа раскрывается) и отдать ей курсор */
  const setVvod = (v: Vvod) => {
    setVvodS(v);
    if (v.parent != null) setRSvern(s0 => { if (!s0.has(v.parent!)) return s0; const n = new Set(s0); n.delete(v.parent!); return n; });
    setFokus(f => f + 1);
  };
  const [selLast, setSelLast] = useState<number | null>(null);
  const [pop, setPop] = useState<
    | { kind: 'explain'; row: Row; at: Anchor }
    | { kind: 'dlit'; row: Row; at: Anchor }
    | { kind: 'links'; row: Row; at: Anchor }
    | { kind: 'task'; row: Row; vkladka: 'obzor' | 'zadacha' | 'svyazi' | 'rabota' | 'shema' }
    | { kind: 'svyazi'; name: string; at: Anchor }
    | { kind: 'bulklinks'; at: Anchor }
    | { kind: 'rsvyazi'; gid: number; at: Anchor }
    | null>(null);
  // «Расчётный»: сроки по одной модели, будто рамок нет (2026-09-09)
  const [bezRamok, setBezRamok] = useState<Record<number, { n: string | null; o: string | null }> | null>(null);
  const [scroll, setScroll] = useState(0);
  const [height, setHeight] = useState(600);
  // высота нижней панели деталей (2026-09-14): тянется мышью, запоминается
  const [detH, setDetH] = useState(loadDetH);
  // сколько пикселей занято над таблицей — нужно, чтобы панель влезла в экран
  const [sverhu, setSverhu] = useState(0);
  const can = ctx.canPlan;
  const cols = ctx.planCols || { hand: true, res: false, pred: false, fakt: false, sprav: false, base: false, noSum: false };

  const fail = (m: string) => ctx.onError?.(m);
  // карточка задачи — всегда нижней панелью, появляется сама при клике по задаче
  // (владелец 2026-09-16: значок «Панель снизу» убран; с 2026-09-14 панель включалась им)
  const panel = pop?.kind === 'task';
  const detVnizu = panel && pop?.kind === 'task';
  const badDate = () => fail('Дата не разобрана: нужно ДД.ММ.ГГГГ');

  /** тянуть левую границу правой панели деталей: влево — панель шире (владелец 2026-10-18: «меню с
   *  деталями — справа, а не снизу»). detH теперь хранит ширину панели.
   *  Слушатели вешаются на документ, иначе быстрый увод мыши рвёт перетаскивание. */
  function tyanutPanel(e: React.MouseEvent) {
    e.preventDefault();
    const x0 = e.clientX, h0 = detH;
    const move = (ev: MouseEvent) =>
      setDetH(Math.min(DET_MAX, Math.max(DET_MIN, h0 + (x0 - ev.clientX))));
    const up = (ev: MouseEvent) => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      const h = Math.min(DET_MAX, Math.max(DET_MIN, h0 + (x0 - ev.clientX)));
      try { localStorage.setItem(DET_KEY, String(h)); } catch { /* приватный режим */ }
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  }
  const saved = (m?: string) => { ctx.onSaved?.(m); ctx.onError?.(''); };

  /** Очистить факт задач (просьба владельца 2026-09-11): планер стирает статус, даты
   *  и процент факта прямо из модели — той же пакетной записью, что и шахматка
   *  (журнал факта ведётся). Статус из слепка СБ3 31.08 так не стирается — он живёт
   *  не в задаче, а в слепке; об этом честно предупреждаем в окне. */
  async function ochistitFakt(ids: number[]) {
    const vybrannye = rows.filter(r => ids.includes(r.id));
    const sFaktom = vybrannye.filter(estFakt);
    if (!sFaktom.length) return;
    const izSlepka = vybrannye.filter(r => r.status && !r.sv).length;
    const odna = sFaktom.length === 1 ? sFaktom[0] : null;
    const da = await confirmBox(odna
      ? 'Очистить факт задачи № ' + (odna.nomer ?? '') + ' «' + odna.rabota + '»'
        + (odna.etazh != null ? ', эт. ' + odna.etazh : '') + '?'
      : 'Очистить факт у задач: ' + sFaktom.length + '?', {
      podpis: 'Стираются статус, даты начала и окончания и процент факта; комментарий '
        + 'и подрядчик остаются. Запись попадёт в «Журнал факта»; стрелкой «назад» это можно отменить. '
        + 'Даты графика обновятся после «Пересчитать».'
        + (izSlepka ? '\n\nУ ' + izSlepka + ' из выбранных статус взят из слепка СБ3 31.08 — '
          + 'он хранится не в задаче и этой кнопкой не стирается.' : ''),
      ok: 'Очистить факт', opasno: true,
    });
    if (!da) return;
    const r = await call<{ izmeneno: number }>('post', p('/api/projects/{pid}/fakt/bulk', { pid: ctx.pid }),
      { ids: sFaktom.map(x => x.id), revs: Object.fromEntries(sFaktom.map(x => [x.id, x.rev])),
        status: null, nachalo: null, okonchanie: null, procent: null });
    if (!r.ok) { fail(r.error.message); return; }
    saved('Факт очищен у задач: ' + r.data.izmeneno + '. Нажмите «Пересчитать», чтобы обновить даты.');
    setSel(new Set()); setSelLast(null);
    void load();
  }

  // номер последнего чтения: ответ устаревшего запроса отбрасывается (2026-10-15 — при быстрой
  // смене проекта через медленную сеть старый ответ приходил позже и затирал справочник работ
  // нового проекта; строка «＋ новая работа» брала образец из чужого проекта)
  const loadSeq = useRef(0);
  // в ручном планировании задачи без корпуса (на объект — так набирают котлован, подготовку) видны при любом
  // выбранном корпусе (#128, 2026-10-27): иначе только что набранная задача пропадала, пока выбран «К1»
  const korpusZaprosa = ruch && ctx.korpus && ctx.korpus !== '*' && !ctx.korpus.split(',').includes('Объект')
    ? ctx.korpus + ',Объект' : (ctx.korpus || '');
  const load = useCallback(async () => {
    const moj = ++loadSeq.current;
    const [t, w, s, sp, h, sv] = await Promise.all([
      call<Tasks>('get', p('/api/projects/{pid}/tasks', { pid: ctx.pid })
        + '?korpus=' + encodeURIComponent(korpusZaprosa) + '&order=name&limit=20000'),
      call<Work[]>('get', p('/api/projects/{pid}/works', { pid: ctx.pid })),
      call<Record<string, Floor[]>>('get', p('/api/projects/{pid}/structure', { pid: ctx.pid })),
      call<{ kod: string; znachenie: string }[]>('get', p('/api/projects/{pid}/spiski', { pid: ctx.pid })),
      call<Wbs>('get', p('/api/projects/{pid}/wbs', { pid: ctx.pid })),
      call<SvodRow[]>('get', '/api/link-templates/rows?project=' + ctx.pid),
    ]);
    if (ruch) {   // группы дерева ручного планирования (#126) и связи с группами (#127)
      const [g, sv] = await Promise.all([
        call<RG[]>('get', p('/api/projects/{pid}/ruch-gruppy', { pid: ctx.pid })),
        call<RSv[]>('get', p('/api/projects/{pid}/ruch/svyazi', { pid: ctx.pid })),
      ]);
      if (moj !== loadSeq.current) return;
      if (g.ok) setRGruppy(g.data);
      if (sv.ok) setRSvyazi(sv.data);
    }
    if (moj !== loadSeq.current) return;   // проект или фильтр успели смениться — этот ответ уже не нужен
    if (!t.ok) { fail(t.error.message); return; }
    if (h.ok) setWbs(h.data.uzly);
    if (sv.ok) setSvod(sv.data);
    if (w.ok) { const m: Record<string, Work> = {}; w.data.forEach(x => { m[x.name] = x; }); setMeta(m); }
    if (s.ok) setStruct(s.data);
    if (sp.ok) setPodr(sp.data.filter(x => x.kod === 'podryadchik').map(x => x.znachenie).filter(Boolean));
    setData(t.data);
    ctx.onTasks?.(t.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.korpus, ctx.reloadToken]);
  useEffect(() => { void load(); }, [load]);

  // расчётный график считается на сервере в памяти, ничего не меняя
  useEffect(() => {
    if (!raschet) { setBezRamok(null); return; }
    let zhiv = true;
    void (async () => {
      const r = await call<{ stroki: { id: number; nachalo: string | null; okonchanie: string | null }[] }>(
        'get', p('/api/projects/{pid}/raschet', { pid: ctx.pid }));
      if (!zhiv) return;
      if (!r.ok) { fail(r.error.message); return; }
      const m: Record<number, { n: string | null; o: string | null }> = {};
      r.data.stroki.forEach(x => { m[x.id] = { n: x.nachalo, o: x.okonchanie }; });
      setBezRamok(m);
    })();
    return () => { zhiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raschet, ctx.pid, ctx.reloadToken]);

  // Esc закрывает нижнюю панель деталей — у окна это делает сам диалог,
  // а панель живёт в потоке страницы и клавиш не слышит (2026-09-14)
  useEffect(() => {
    if (!detVnizu) return;
    const esc = (e: KeyboardEvent) => {
      // поверх панели может стоять окно (карточка этажа, работы, диалог) —
      // тогда Esc принадлежит ему, панель не трогаем
      if (e.key === 'Escape' && !document.querySelector('.dlg-fon')) setPop(null);
    };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [detVnizu]);

  /** Задачи этой работы заведены руками, а не раскаткой (2026-09-10): признак
   *  живёт у работы — «Раскатка: вручную» или контур «Ручной». Такую задачу
   *  можно удалить поштучно; раскатанная вернётся при следующем событии. */
  const ruchnayaRabota = (name: string) => {
    const w = meta[name];
    return !!w && (w.raskatka === 'вручную' || kontury(w.kontur).includes('Ручной'));
  };

  // клиентские фильтры оболочки (работа, этаж, подрядчик); на экране «Ручное
  // планирование» — только задачи работ с раскаткой «вручную» (2026-09-08)
  useEffect(() => {
    if (!data) { setRows([]); return; }
    const poFiltram = data.rows.filter(r => (!ctx.matchesTask || ctx.matchesTask(r))
      && (!ruch || ruchnayaRabota(r.rabota))
      && (!kritColumn || !tolkoKrit || !kritMap.size || critical(r)));
    const sProblemoj = poFiltram.filter(r => problemy.has(r.id));
    const vidy = new Map<string, { chto: string; gruppa: string; n: number }>();
    for (const r of sProblemoj) {
      const pr = problemy.get(r.id)!;
      for (const chto of pr.chto) {
        const v = vidy.get(chto) || { chto, gruppa: pr.gruppa, n: 0 };
        v.n += 1;
        vidy.set(chto, v);
      }
    }
    // сначала ошибки, внутри — по числу задач
    onOshibki?.({ vsego: sProblemoj.length, vidy: [...vidy.values()].sort((a, b) =>
      (a.gruppa === 'ошибка' ? 0 : 1) - (b.gruppa === 'ошибка' ? 0 : 1) || b.n - a.n) });
    const keep = !oshibki ? poFiltram
      : sProblemoj.filter(r => !vidOshibki || problemy.get(r.id)!.chto.includes(vidOshibki));
    // в режиме «Расчётный» показываем даты без рамок — сами данные не трогаем
    setRows(raschet && bezRamok
      ? keep.map(r => (bezRamok[r.id]
        ? { ...r, nachalo: bezRamok[r.id].n, okonchanie: bezRamok[r.id].o } : r))
      : keep);
    setSel(prev => { const alive = new Set(keep.map(r => r.id)); return new Set([...prev].filter(x => alive.has(x))); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ctx.filterKey, ruch, meta, raschet, bezRamok, oshibki, vidOshibki, problemy, kritColumn, tolkoKrit, kritMap]);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const set = () => {
      setHeight(el.clientHeight || 600);
      // сколько экрана занято над таблицей (шапка, фильтры, полоса кнопок):
      // по этой мерке таблица отдаёт панели деталей ровно столько, сколько
      // нужно, и панель не уезжает за нижний край окна (2026-09-14)
      const r = el.getBoundingClientRect();
      setSverhu(Math.max(0, Math.round(r.top + window.scrollY)));
    };
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    window.addEventListener('resize', set);
    return () => { ro.disconnect(); window.removeEventListener('resize', set); };
    // вид меняет, какой блок измеряется: таблица или сетка шахматки (2026-09-15)
  }, [vid]);

  const planKorpus = data && (data.korpus === '*' || (data.korpus || '').includes(',')) ? '' : (data?.korpus || '');
  const etapOf = useCallback((r: Row) => meta[r.rabota]?.etap || NO_ETAP, [meta]);
  // иерархия (2026-10-06): путь работы — имена разделов сверху вниз, порядок — как в структуре.
  // Работа вне иерархии — в группе «без раздела» в конце; проект без иерархии — по этапам, как раньше
  const ier = useMemo(() => {
    const put = new Map<string, string[]>(), por = new Map<string, number>(), porRazd = new Map<string, number>();
    const stek: string[] = [];
    wbs.forEach((u, i) => {
      stek.length = u.glubina; stek.push(u.name);
      if (u.razdel && !porRazd.has(u.name)) porRazd.set(u.name, i);
      if (!u.razdel && u.work_id != null) { put.set(u.name, stek.slice(0, -1)); por.set(u.name, i); }
    });
    return { put, por, porRazd, est: wbs.length > 0 };
  }, [wbs]);
  // свои разбивки: работа → путь разделов, раздел → порядок
  const rzPut = useMemo(() => {
    const m = new Map<number, { put: Map<string, string[]>; porRazd: Map<string, number> }>();
    wbsRz.forEach((uzly, id) => {
      const put = new Map<string, string[]>(), porRazd = new Map<string, number>(), stek: string[] = [];
      uzly.forEach((u, i) => {
        stek.length = u.glubina; stek.push(u.name);
        if (u.razdel && !porRazd.has(u.name)) porRazd.set(u.name, i);
        if (!u.razdel && u.work_id != null) put.set(u.name, stek.slice(0, -1));
      });
      m.set(id, { put, porRazd });
    });
    return m;
  }, [wbsRz]);
  // строки ГПР (#119/#120): работа → её строка, только если в строке больше одной работы (иначе обычная работа)
  const strokaRab = useMemo(() => {
    const cnt = new Map<string, number>();
    Object.values(meta).forEach(w => { const s0 = w.stroka_gpr; if (s0) cnt.set(s0, (cnt.get(s0) || 0) + 1); });
    const m = new Map<string, string>();
    Object.values(meta).forEach(w => { const s0 = w.stroka_gpr; if (s0 && (cnt.get(s0) || 0) > 1) m.set(w.name, s0); });
    return m;
  }, [meta]);
  // уровни группировки: путь строк-итогов; работа последним уровнем — прежняя строка работы
  const sRabotoj = cep.length > 0 && cep[cep.length - 1] === 'rabota';
  const urovni = useMemo(() => (sRabotoj ? cep.slice(0, -1) : cep.slice()), [cepKl, sRabotoj]);   // eslint-disable-line react-hooks/exhaustive-deps
  const NZ = '— не задано —';
  const KAL_P: Record<string, string> = { 'календарные': 'Календарные дни', 'без праздников': 'Без праздников' };
  const znachUrovnya = useCallback((k: string, r: Row): string => {
    const w = meta[r.rabota] as Record<string, unknown> | undefined;
    const t = (v: unknown) => String(v ?? '').trim() || NZ;
    if (k === 'etap') return ier.est ? ((ier.put.get(r.rabota) || []).join(' › ') || VNE_IER) : etapOf(r);
    if (k.startsWith('rz:')) return (rzPut.get(+k.slice(3))?.put.get(r.rabota) || []).join(' › ') || NZ;
    if (k === 'rabota') return r.rabota || NZ;
    if (k === 'stroka') return strokaRab.get(r.rabota) || r.rabota || NZ;   // работа без строки — сама себе строка
    if (k === 'korpus') return r.korpus || planKorpus || NZ;
    if (k === 'podr') return t(r.podryadchik);
    if (k === 'raskatka') { const v = String(w?.raskatka ?? ''); return (RASK_LABEL as Record<string, string>)[v] || t(v); }
    if (k === 'kalendar') { const v = String(w?.kalendar ?? ''); return KAL_P[v] || t(v); }
    if (k === 'logika') return t(w?.logika) === NZ ? 'типовые снизу вверх' : t(w?.logika);
    return t(w?.[k]);
  }, [meta, ier, rzPut, etapOf, planKorpus, strokaRab]);   // eslint-disable-line react-hooks/exhaustive-deps
  const putOf = useCallback((r: Row): string[] => urovni.map(k => znachUrovnya(k, r)), [urovni, znachUrovnya]);
  const klyuchPuti = (put: string[], do_: number) => put.slice(0, do_ + 1).join('¶');
  /** отступ работы по глубине — как у строки иерархии (владелец 2026-10-06: «не понятно, что
      Стояки К2 под Водоотведением»); без иерархии — один уровень под этапом */
  const glubinaRaboty = (_name: string) => urovni.length;
  const floorOf = useCallback((r: Row) => (struct[r.korpus || planKorpus] || []).find(f => String(f.etazh) === String(r.etazh)),
    [struct, planKorpus]);

  const SORT_GET: ((r: Row) => string | number)[] = useMemo(() => [
    r => r.nomer ?? 1e12, r => r.korpus || planKorpus, r => parseFloat(String(r.etazh)) || String(r.etazh ?? ''),
    r => r.sek || r.zahvatka || '', r => r.rabota || '', r => r.nachalo || '', r => r.okonchanie || '', r => r.dlit ?? -1,
    r => (r.iskl ? 1 : 0), r => r.dlit_r ?? -1, r => r.dd ?? -1, r => r.obyom ?? -1, r => r.prior ?? -1e12, r => r.brigada ?? -1,
    r => r.zapas ?? -1, r => r.nach_vr || '', r => r.okonch_vr || '', r => predRuchText(r), r => r.pos ?? 1e12,
    r => r.ps || '', r => r.prs || '', r => r.nach_vr || '', r => r.okonch_vr || '', r => predRuchText(r),
    r => r.podryadchik || '', r => r.vn || '', r => r.vo || '', r => r.sv || '', r => r.proc ?? -1, r => r.dlit_avto ?? -1,
    r => floorOf(r)?.prioritet ?? -1e12, r => r.bln || '', r => r.bl || '',
  ], [planKorpus, floorOf]);

  // очередь «Оч.» — позиция в рамках корпуса и работы
  const rank = useMemo(() => {
    const byWk = new Map<string, Row[]>();
    rows.forEach(r => {
      const k = (r.korpus || planKorpus) + '¶' + r.rabota;
      if (!byWk.has(k)) byWk.set(k, []);
      byWk.get(k)!.push(r);
    });
    const m = new Map<number, number | ''>();
    byWk.forEach(arr => arr.slice().sort((a, b) => (a.pos ?? 1e9) - (b.pos ?? 1e9))
      .forEach((r, i) => m.set(r.id, r.pos == null ? '' : i + 1)));
    return m;
  }, [rows, planKorpus]);

  // плоский список того, что рисуем: строки этапов, работ и задач
  const poRabote = useMemo(() => {
    const m = new Map<string, Row[]>();
    rows.forEach(r => { const k = r.rabota || ''; (m.get(k) || m.set(k, []).get(k)!).push(r); });
    return m;
  }, [rows]);

  // ключ раздела модели (путь имён) ↔ строка иерархии: свёрнутые разделы одни и те же в обоих режимах
  const putStroki = useCallback((i: number) => {
    const rs = ie.rows, put: string[] = [];
    let g = rs[i].glubina;
    put.unshift(rs[i].name);
    for (let j = i - 1; j >= 0 && g > 0; j--) if (rs[j].glubina < g) { g = rs[j].glubina; put.unshift(rs[j].name); }
    return put.join('¶');
  }, [ie.rows]);
  const bylIerarh = useRef(false);
  useEffect(() => {
    if (ierarh && !bylIerarh.current && ie.rows.length) {
      // вход: свернуть в иерархии то, что свёрнуто в графике
      const svern = new Set<string>();
      ie.rows.forEach((r, i) => { if (ierEstDeti(ie.rows, i) && closedEtap.has(putStroki(i))) svern.add(r.klyuch); });
      ie.svernuto.forEach(k => { if (!svern.has(k)) ie.svernut(k); });
      svern.forEach(k => { if (!ie.svernuto.has(k)) ie.svernut(k); });
      bylIerarh.current = true;
    } else if (!ierarh && bylIerarh.current) {
      // выход: перенести свёрнутое обратно в график
      const zakr = new Set<string>();
      ie.rows.forEach((r, i) => { if (ierEstDeti(ie.rows, i) && ie.svernuto.has(r.klyuch)) zakr.add(putStroki(i)); });
      setClosedEtap(zakr);
      bylIerarh.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ierarh, ie.rows.length]);

  // сроки строки структуры: работа — по своим задачам, раздел — по задачам всех работ под ним
  const ierSroki = useMemo(() => {
    const m = new Map<string, { min?: string; max?: string; spisok: Row[] }>();
    if (!ierarh) return m;
    const rs = ie.rows;
    rs.forEach((r, i) => {
      if (!r.name.trim()) return;
      const kon = ierKonec(rs, i);
      const spisok: Row[] = [];
      for (let j = i; j < kon; j++) if (!ierEstDeti(rs, j)) spisok.push(...(poRabote.get(rs[j].name) || []));
      let min: string | undefined, max: string | undefined;
      spisok.forEach(x => {
        if (x.nachalo && (!min || x.nachalo < min)) min = x.nachalo;
        if (x.okonchanie && (!max || x.okonchanie > max)) max = x.okonchanie;
      });
      m.set(r.klyuch, { min, max, spisok });
    });
    return m;
  }, [ierarh, ie.rows, poRabote]);

  // дерево ручного планирования (#126, 2026-10-27): группы и задачи одного родителя — по общей шкале
  // порядка (как на сервере: порядок, затем №); строка ввода — после своей строки `vvod.posle`
  const rgRoditel = useMemo(() => new Map(rGruppy.map(g => [g.id, g.parent_id ?? null])), [rGruppy]);
  const derevo: Display[] = useMemo(() => {
    if (!ruch) return [];
    type El = { g?: RG; r?: Row; por: number; nm: number };
    const deti = new Map<number | null, El[]>();
    const dob = (k: number | null, e: El) => { const a = deti.get(k); if (a) a.push(e); else deti.set(k, [e]); };
    rGruppy.forEach(g => dob(g.parent_id != null && rgRoditel.has(g.parent_id) ? g.parent_id : null,
      { g, por: g.poryadok, nm: 0 }));
    rows.forEach(r => dob(r.rg != null && rgRoditel.has(r.rg) ? r.rg : null,
      { r, por: r.rp ?? 1e9, nm: r.nomer ?? 0 }));
    deti.forEach(a => a.sort((x, y) => x.por - y.por || x.nm - y.nm));
    const sroki = (gid: number): { min?: string; max?: string; n: number } => {
      let min: string | undefined, max: string | undefined, n = 0;
      for (const e of deti.get(gid) || []) {
        const sr = e.g ? sroki(e.g.id) : { min: e.r!.nachalo || undefined, max: e.r!.okonchanie || undefined, n: 1 };
        n += sr.n;
        if (sr.min && (!min || sr.min < min)) min = sr.min;
        if (sr.max && (!max || sr.max > max)) max = sr.max;
      }
      return { min, max, n };
    };
    const out: Display[] = [];
    let vvodEst = false;
    const vvodSyuda = (glubina: number) => { if (can && !vvodEst) { out.push({ kind: 'vvod', glubina }); vvodEst = true; } };
    const obhod = (parent: number | null, glubina: number) => {
      const moj = vvod.parent === parent;
      if (moj && vvod.posle === 'nachalo') vvodSyuda(glubina);
      for (const e of deti.get(parent) || []) {
        if (e.g) {
          const open = !rSvern.has(e.g.id);
          out.push({ kind: 'rgr', id: e.g.id, name: e.g.name, glubina, open, ...sroki(e.g.id) });
          if (open) obhod(e.g.id, glubina + 1);
        } else out.push({ kind: 'task', row: e.r!, glubina });
        const ps = vvod.posle;
        if (moj && ps && ps !== 'nachalo' && (e.g ? ps.tip === 'gruppa' && ps.id === e.g.id : ps.tip === 'task' && ps.id === e.r!.id))
          vvodSyuda(glubina);
      }
      if (moj) vvodSyuda(glubina);   // posle — в конец (или строку, после которой стоял ввод, удалили)
    };
    obhod(null, 0);
    vvodSyuda(0);                     // группа строки ввода свёрнута или удалена — внизу
    return out;
  }, [ruch, rGruppy, rgRoditel, rows, rSvern, vvod, can]);
  /** все задачи группы дерева, с вложенными группами */
  const zadachiGruppy = (gid: number) => rows.filter(r => {
    for (let x: number | null = r.rg ?? null; x != null; x = rgRoditel.get(x) ?? null) if (x === gid) return true;
    return false;
  });
  const roditel = (u: Uzel): number | null => (u.tip === 'gruppa'
    ? rGruppy.find(g => g.id === u.id)?.parent_id ?? null : rows.find(r => r.id === u.id)?.rg ?? null);

  const display: Display[] = useMemo(() => {
    if (ierarh) {
      // та же структура, что в графике: разделы, работы и задачи раскрытых работ
      // (владелец 2026-10-06: «чтобы таблица не скакала»)
      // фильтры панели действуют и здесь (владелец 2026-10-07): работа вне фильтра не показывается,
      // раздел — только если под ним есть подходящие работы; пустая строка для набора остаётся
      const filtr = !!ctx.filterActive && !!ctx.matchesWork;
      const podhodit = (i: number) => {
        const r = ie.rows[i];
        if (!r.name.trim()) return true;
        if (!ierEstDeti(ie.rows, i)) return !filtr || ctx.matchesWork!(r.name);
        const kon = ierKonec(ie.rows, i);
        for (let j = i + 1; j < kon; j++) if (!ierEstDeti(ie.rows, j) && ie.rows[j].name.trim() && (!filtr || ctx.matchesWork!(ie.rows[j].name))) return true;
        return !filtr;
      };
      const out: Display[] = [];
      ie.rows.forEach((r, i) => {
        if (!ie.vidim[i] || !podhodit(i)) return;
        const razdel = ierEstDeti(ie.rows, i);
        out.push({ kind: 'ier', i, name: r.name, key: r.klyuch, razdel, ...ierSroki.get(r.klyuch) });
        // задачи по этажам в этом режиме не раскрываются (владелец 2026-10-06)
      });
      return out;
    }
    // в ручном вводе группировки нет: только внесённые вручную задачи списком.
    // Сортировка по столбцу (#54, 2026-09-13) группы больше не снимает: задачи
    // сортируются внутри своей работы, суммарные строки остаются (раньше глаз
    // «показать суммарные» при сортировке ничего не менял)
    const grouped = cep.length > 0 && !ruch;   // «Без группировки» — пустая цепочка (2026-10-17)
    const poSortu = (arr: Row[]) => (sort.length ? arr.slice().sort((a, b) => {
      for (const s of sort) {          // уровни по порядку: первый главный
        const g = SORT_GET[s.i];
        const x = g(a), y = g(b);
        if (x < y) return -1 * s.dir;
        if (x > y) return 1 * s.dir;
      }
      return 0;
    }) : arr);
    if (ruch) return derevo;
    if (!grouped) {
      return poSortu(rows).map(row => ({ kind: 'task', row } as Display));
    }
    // порядок по уровням группировки (2026-10-17): этапы и разделы — как в разбивке, работы — как в основной
    // разбивке (без неё — по самой ранней дате), корпуса — как в структуре, прочее — по алфавиту; «не задано» — в конце
    const wStart = new Map<string, string>();
    rows.forEach(r => { const s0 = r.nachalo || '9999'; if (!wStart.has(r.rabota) || s0 < wStart.get(r.rabota)!) wStart.set(r.rabota, s0); });
    const korpPor = new Map(Object.keys(struct).map((k, i) => [k, i]));
    const rabPor = (n: string) => ier.por.get(n) ?? 1e9;
    const rang = (k: string, v: string, r: Row): number | string => {
      if (v === NZ || v === VNE_IER) return '\uffff';
      if (k === 'etap') return ier.est ? (ier.porRazd.get(v.split(' › ').slice(-1)[0]) ?? rabPor(r.rabota)) : v.toLowerCase();
      if (k.startsWith('rz:')) return rzPut.get(+k.slice(3))?.porRazd.get(v.split(' › ').slice(-1)[0]) ?? 1e9;
      if (k === 'rabota') return ier.est ? rabPor(v) : (wStart.get(v) || '9999');
      if (k === 'korpus') return korpPor.get(v) ?? 1e9;
      return v.toLowerCase();
    };
    const sravn = (x: number | string, y: number | string) => (typeof x === typeof y ? (x < y ? -1 : x > y ? 1 : 0) : typeof x === 'number' ? -1 : 1);
    // строка ГПР → место самой ранней её работы (порядок «Этапов» или дата) и её сроки по всем задачам
    const lider = new Map<string, number | string>();
    const srokiStroki = new Map<string, { min?: string; max?: string; rabot: Set<string> }>();
    rows.forEach(r => {
      const s0 = strokaRab.get(r.rabota);
      if (!s0) return;
      const v = rang('rabota', r.rabota, r);
      const l = lider.get(s0);
      if (l === undefined || sravn(v, l) < 0) lider.set(s0, v);
      const ss = srokiStroki.get(s0) || { rabot: new Set<string>() };
      ss.rabot.add(r.rabota);
      if (r.nachalo && (!ss.min || r.nachalo < ss.min)) ss.min = r.nachalo;
      if (r.okonchanie && (!ss.max || r.okonchanie > ss.max)) ss.max = r.okonchanie;
      srokiStroki.set(s0, ss);
    });
    const sorted = poSortu(rows).sort((a, b) => {   // устойчиво: внутри группы — порядок сортировки
      const pa = putOf(a), pb = putOf(b);
      for (let i = 0; i < urovni.length; i++) {
        if (pa[i] === pb[i]) continue;
        const c = sravn(rang(urovni[i], pa[i], a), rang(urovni[i], pb[i], b));
        if (c) return c;
        return pa[i] < pb[i] ? -1 : 1;
      }
      // внутри группы — порядок работ из структуры этапов, и когда строки работы нет (группировка без
      // «Работы»: задачи прямо под этапом шли по алфавиту — владелец 2026-10-18)
      if (a.rabota !== b.rabota) {
        // строка ГПР (#120): её работы идут подряд, на месте самой ранней из них
        if (sRabotoj) {
          const sa = strokaRab.get(a.rabota), sb = strokaRab.get(b.rabota);
          if (sa !== sb) {
            const c0 = sravn(sa ? lider.get(sa)! : rang('rabota', a.rabota, a), sb ? lider.get(sb)! : rang('rabota', b.rabota, b));
            if (c0) return c0;
            return (sa || a.rabota) < (sb || b.rabota) ? -1 : 1;
          }
        }
        const c = sravn(rang('rabota', a.rabota, a), rang('rabota', b.rabota, b));
        return c || (a.rabota < b.rabota ? -1 : 1);
      }
      return 0;
    });
    // сроки раздела — по всем задачам под ним (на любой глубине)
    const srokiRazdela = new Map<string, { min?: string; max?: string }>();
    sorted.forEach(x => {
      const put = putOf(x);
      put.forEach((_, i) => {
        const k = klyuchPuti(put, i);
        const s = srokiRazdela.get(k) || {};
        if (x.nachalo && (!s.min || x.nachalo < s.min)) s.min = x.nachalo;
        if (x.okonchanie && (!s.max || x.okonchanie > s.max)) s.max = x.okonchanie;
        srokiRazdela.set(k, s);
      });
    });
    const out: Display[] = [];
    let curPut: string[] = [], curWork: string | null = null, curStroka: string | null = null;
    sorted.forEach(row => {
      const put = putOf(row);
      let obshchih = 0;
      while (obshchih < put.length && obshchih < curPut.length && put[obshchih] === curPut[obshchih]) obshchih++;
      if (obshchih < put.length || put.length !== curPut.length) { curWork = null; curStroka = null; }
      // свёрнутый раздел выше — его содержимое не показываем
      const zakryt = (do_: number) => { for (let i = 0; i <= do_; i++) if (closedEtap.has(klyuchPuti(put, i))) return true; return false; };
      for (let i = obshchih; i < put.length; i++) {
        if (zakryt(i - 1)) break;
        const k = klyuchPuti(put, i);
        out.push({ kind: 'etap', name: put[i], key: k, glubina: i, ...srokiRazdela.get(k) });
      }
      curPut = put;
      if (zakryt(put.length - 1)) return;
      if (!sRabotoj) { out.push({ kind: 'task', row }); return; }   // без строки работы — задачи под последним уровнем
      // строка ГПР (#120): суммарная строка над работами строки, по умолчанию свёрнута — в графике одна позиция
      const str = strokaRab.get(row.rabota) || null;
      const strKl = str ? klyuchPuti(put, put.length - 1) + '¶ГПР¶' + str : '';
      if (str !== curStroka) {
        curStroka = str;
        if (str) {
          const ss = srokiStroki.get(str);
          out.push({ kind: 'stroka', name: str, key: strKl, open: openStroki.has(strKl), rabot: ss?.rabot.size || 0,
            min: ss?.min, max: ss?.max });
        }
      }
      if (str && !openStroki.has(strKl)) return;
      if (row.rabota !== curWork) {
        curWork = row.rabota;
        const wr = rows.filter(x => x.rabota === curWork);
        out.push({ kind: 'work', name: row.rabota, open: openWorks.has(row.rabota), vStroke: !!str,
          min: wr.map(x => x.nachalo).filter(Boolean).sort()[0] as string | undefined,
          max: wr.map(x => x.okonchanie).filter(Boolean).sort().slice(-1)[0] as string | undefined });
      }
      if (!openWorks.has(row.rabota)) return;
      out.push({ kind: 'task', row });
    });
    return out;
  }, [rows, sort, cepKl, urovni, sRabotoj, struct, rzPut, closedEtap, openWorks, openStroki, strokaRab, etapOf, putOf, ier, SORT_GET, ierarh, ie.rows, ie.vidim, ierSroki, poRabote, ctx, derevo]);   // eslint-disable-line react-hooks/exhaustive-deps

  // «Уровень» (2026-10-17): раскрыть до уровня n — строки глубже свёрнуты; n = длина цепочки — до задач
  useEffect(() => {
    if (!uroven) return;
    const n = uroven.n;
    if (n < urovni.length) {
      const zakr = new Set<string>();
      rows.forEach(r => zakr.add(klyuchPuti(putOf(r), n)));
      setClosedEtap(zakr); setOpenWorks(new Set());
    } else if (sRabotoj && n === urovni.length) {
      setClosedEtap(new Set()); setOpenWorks(new Set());
    } else {
      setClosedEtap(new Set()); setOpenWorks(new Set(rows.map(r => r.rabota)));
      // до задач — строки ГПР тоже раскрыты (#120)
      setOpenStroki(new Set(rows.filter(r => strokaRab.has(r.rabota))
        .map(r => klyuchPuti(putOf(r), putOf(r).length - 1) + '¶ГПР¶' + strokaRab.get(r.rabota))));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uroven?.t]);
  // новая цепочка — ключи итогов другие: свёрнутое прежней цепочки не переносим
  const bylaCep = useRef(cepKl);
  useEffect(() => { if (bylaCep.current !== cepKl) { bylaCep.current = cepKl; setClosedEtap(new Set()); } }, [cepKl]);

  // окно виртуализации
  // пары «предшественник → задача» для стрелок: считает ядро теми же правилами,
  // что и даты, поэтому стрелка показывает учтённую связь, а не догадку
  const [paryList, setParyList] = useState<Para[]>([]);
  useEffect(() => {
    // на холсте стрелки видны у полосы под мышью, поэтому пары нужны всегда
    if (!(svyazi || holst) || vid !== 'gant') { setParyList([]); return; }
    let zhiv = true;
    void (async () => {
      const r = await call<{ pary: Para[] }>('get',
        p('/api/projects/{pid}/svyazi-zadach', { pid: ctx.pid }));
      if (zhiv && r.ok) setParyList(r.data.pary || []);
      if (!r.ok) fail(r.error.message);
    })();
    return () => { zhiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svyazi, holst, vid, ctx.pid, ctx.reloadToken]);

  // Задачи с замечаниями (ошибки и конфликты): подсвечиваем строку прямо в
  // модели, чтобы «косяки планирования» были видны сразу, а не только на
  // экране «Проверки» (просьба владельца 2026-09-10). Перечитываем после
  // каждой правки и пересчёта — вместе с самими задачами.
  useEffect(() => {
    let zhiv = true;
    void (async () => {
      const r = await call<{ zadachi: Problema[] }>('get',
        p('/api/projects/{pid}/problemy', { pid: ctx.pid }));
      if (!zhiv) return;
      if (r.ok) setProblemy(new Map((r.data.zadachi || []).map(z => [z.tid, z])));
      else setProblemy(new Map());        // не смогли — просто не подсвечиваем
    })();
    return () => { zhiv = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.pid, ctx.reloadToken]);

  // какая работа у задачи: нужно стрелкам связей, когда группа свёрнута
  // и вместо самой задачи на экране только суммарная полоса работы
  const rabotaZadachi = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of rows) m.set(r.id, r.rabota);
    return m;
  }, [rows]);

  const first = Math.max(0, Math.floor(scroll / ROW_H) - WINDOW_PAD);
  const last = Math.min(display.length, Math.ceil((scroll + height) / ROW_H) + WINDOW_PAD);
  const padTop = first * ROW_H, padBottom = Math.max(0, (display.length - last) * ROW_H);

  const grouped = cep.length > 0 && !ruch;   // «Без группировки» — пустая цепочка (2026-10-17)
  // --- правая часть: Гант и сводная по месяцам (2026-09-08) ---
  // слева всегда одни и те же колонки, справа меняется только визуализация
  // поэтажная шахматка (2026-09-15) рисуется вместо таблицы, в ряд со строками не встаёт
  const viz = vid === 'gant' || vid === 'pivot';
  const mesyacy = useMemo(() => {
    if (!viz) return [] as { k: string; label: string; d0: number; d1: number }[];
    // «пустая» дата листа (1899-12-30) и прочий мусор шкалу не растягивают
    const ds = (rows.flatMap(r => [r.nachalo, r.okonchanie]).filter(Boolean) as string[])
      .filter(d => d >= '2000-01-01' && d <= '2100-01-01');
    if (!ds.length) return [];
    const min = new Date(ds.reduce((a, b) => (a < b ? a : b)) + 'T00:00:00Z');
    const max = new Date(ds.reduce((a, b) => (a > b ? a : b)) + 'T00:00:00Z');
    const out: { k: string; label: string; d0: number; d1: number }[] = [];
    const cur = new Date(Date.UTC(min.getUTCFullYear(), min.getUTCMonth(), 1));
    while (cur <= max && out.length < 120) {
      const y = cur.getUTCFullYear(), m = cur.getUTCMonth();
      const d0 = Math.round(Date.UTC(y, m, 1) / D1);
      const d1 = Math.round(Date.UTC(y, m + 1, 1) / D1) - 1;
      out.push({ k: y + '-' + m, label: MES[m] + (m === 0 || !out.length ? ' ' + String(y).slice(2) : ''), d0, d1 });
      cur.setUTCMonth(m + 1);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, viz]);
  // холст: шкала линейная в днях (масштаб px), с запасом в две недели справа — есть куда тянуть
  const holstOn = holst && vid === 'gant' && mesyacy.length > 0 && !ierarh;
  // схема связей справа (владелец 2026-10-06): уровень работы — по цепочке связей проекта
  const [vybrSv, setVybrSv] = useState<string | null>(null);
  const svUr = useMemo(() => {
    if (!ierarh) return new Map<string, number>();
    const names = new Set(ie.rows.filter((r, i) => r.name.trim() && !ierEstDeti(ie.rows, i) && r.work_id).map(r => r.name));
    return svUrovni(svod, names);
  }, [ierarh, ie.rows, svod]);
  const svShirina = SV_OTSTUP + (Math.max(0, ...svUr.values()) + 1) * SV_SHAG + 40;
  const [px, setPx] = useState(loadPx);
  const smenitPx = (v: number) => {
    setPx(v);
    try { localStorage.setItem(PX_KEY, String(v)); } catch { /* приватный режим */ }
  };
  const hd0 = mesyacy.length ? mesyacy[0].d0 : 0;
  const hd1 = mesyacy.length ? mesyacy[mesyacy.length - 1].d1 + 14 : 0;
  const shirina = holstOn ? Math.ceil((hd1 - hd0 + 1) * px) : mesyacy.length * MW;
  // Пересчёт «день ↔ пиксель» идёт по фактическим месяцам (2026-09-08): колонка
  // месяца всегда MW, а дней в месяце 28–31, поэтому средний день (MW/30,44)
  // уводил полосы от сетки тем сильнее, чем дальше от начала шкалы.
  const dney_v = (m: { d0: number; d1: number }) => m.d1 - m.d0 + 1;
  /** день (серийный номер) → пиксели от левого края шкалы */
  const xDnya = (d: number) => {
    if (!mesyacy.length) return 0;
    let i = 0;
    while (i < mesyacy.length - 1 && d > mesyacy[i].d1) i++;
    const m = mesyacy[i];
    return i * MW + (d - m.d0) * (MW / dney_v(m));
  };
  /** пиксели → день (серийный номер), с округлением до дня */
  const dnyaX = (x: number) => {
    if (!mesyacy.length) return 0;
    const i = Math.max(0, Math.min(mesyacy.length - 1, Math.floor(x / MW)));
    const m = mesyacy[i];
    return m.d0 + Math.round((x - i * MW) / (MW / dney_v(m)));
  };
  /** геометрия полосы: отступ слева, ширина и дни начала/окончания */
  const polosa = (n?: string | null, o?: string | null) => {
    if (!n || !o || !mesyacy.length) return null;
    const a = dnum(n)!, b = dnum(o)!;
    const left = xDnya(a);
    return { left, width: Math.max(3, xDnya(b + 1) - left), a, b };
  };
  /** сколько задач заканчивается в этом месяце (решение владельца 2026-09-08:
      считаем по дате окончания, чтобы сумма по строке равнялась числу задач,
      а длинная работа не считалась в каждом задетом месяце) */
  const vMesyace = (list: Row[], m: { d0: number; d1: number }) =>
    list.filter(r => r.okonchanie && dnum(r.okonchanie)! >= m.d0 && dnum(r.okonchanie)! <= m.d1).length;

  // строки по работам и этапам — для подсчёта задач в месяце (сводная)

  // --- перетаскивание полос Ганта (2026-09-08): полосу двигаем — ставим ручное
  // начало, тянем за правый край — ручную длительность. Пока тянем, полоса
  // сдвигается на экране; отпустили — правка уходит на сервер.
  const tashim = useRef<{ id: number; x0: number; kray: boolean; row: Row;
                         a: number; b: number } | null>(null);
  // тянем суммарную полосу (режим «Корректировка суммарной», 2026-09-08):
  // храним задачи группы и её границы в днях
  const tashimSum = useRef<{ x0: number; a: number; b: number; spisok: Row[]; kluch: string;
                            kray: boolean } | null>(null);
  const [sdvigSum, setSdvigSum] = useState<{ kluch: string; dney: number; kray: boolean } | null>(null);
  const [sdvig, setSdvig] = useState<{ id: number; dney: number; kray: boolean } | null>(null);
  // полосы, ждущие пересчёта (решение владельца 2026-09-08): даты в базе меняет
  // только «Актуализировать», поэтому сразу после перетаскивания полоса остаётся
  // в новой длине и рисуется пунктиром — до ближайшего пересчёта модели
  const [zhdut, setZhdut] = useState<Record<number, { n: string; o: string }>>({});
  // то же для суммарных полос: ключ группы → ожидаемое окончание (2026-09-08)
  const [zhdutSum, setZhdutSum] = useState<Record<string, { n?: string; o?: string }>>({});
  useEffect(() => { setZhdut({}); setZhdutSum({}); }, [ctx.reloadToken, ctx.pid, ctx.korpus]);

  useEffect(() => {
    if (!can) return;
    // сдвиг в днях: считаем по той же шкале, что рисует полосу, поэтому
    // полоса во время перетаскивания стоит ровно там, где окажется после
    const dneyPoX = (t: NonNullable<typeof tashim.current>, x: number) => {
      const kray = t.kray ? t.b + 1 : t.a;
      return dnyaX(xDnya(kray) + (x - t.x0)) - kray;
    };
    // защита от случайного щелчка (владелец 2026-09-15): пока мышь ушла меньше чем
    // на POROG пикселей, это не перетаскивание — ничего не показываем и не пишем
    const POROG = 6;
    const move = (e: MouseEvent) => {
      const t = tashim.current;
      if (t && Math.abs(e.clientX - t.x0) >= POROG) setSdvig({ id: t.id, dney: dneyPoX(t, e.clientX), kray: t.kray });
      const s2 = tashimSum.current;
      if (s2 && Math.abs(e.clientX - s2.x0) >= POROG) {
        const opora = s2.kray ? s2.b + 1 : s2.a;
        const nov = dnyaX(xDnya(opora) + (e.clientX - s2.x0));
        setSdvigSum({ kluch: s2.kluch, dney: nov - opora, kray: s2.kray });
      }
    };
    const up = async (e: MouseEvent) => {
      const s2 = tashimSum.current;
      tashimSum.current = null;
      setSdvigSum(null);
      if (s2 && Math.abs(e.clientX - s2.x0) < POROG) return;   // щелчок, а не перетаскивание
      if (s2 && mesyacy.length) {
        const opora = s2.kray ? s2.b + 1 : s2.a;
        await tyanulGruppu(s2.spisok, s2.kluch, s2.a, s2.b, s2.kray,
          dnyaX(xDnya(opora) + (e.clientX - s2.x0)));
        return;
      }
      const t = tashim.current;
      tashim.current = null;
      setSdvig(null);
      if (!t || !mesyacy.length) return;
      if (Math.abs(e.clientX - t.x0) < POROG) return;             // щелчок, а не перетаскивание
      const dney = dneyPoX(t, e.clientX);
      if (!dney) return;
      await tyanulZadachu(t.row, t.kray, dney);
    };
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    return () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [can, mesyacy, rows]);

  /** Записать перетаскивание полосы задачи (общее для старого Ганта и холста):
   *  правый край — ручная длительность, вся полоса — ручное начало. Без подтверждения
   *  (владелец 2026-09-15: «после каждой правки не надо спрашивать»): от случайной даты
   *  защищает порог POROG, ошибку снимает стрелка «назад». */
  async function tyanulZadachu(r: Row, kray: boolean, dney: number) {
    if (kray) {
      const dlit = Math.max(1, Math.round((r.dlit || 1) + dney));
      const ok = await sohranit(r, { dlit_ruchnaya: dlit },
        'Длительность «' + r.rabota + '» эт. ' + r.etazh + ': ' + dlit + ' дн.');
      if (ok && r.nachalo) zhdatPolosu(r.id, r.nachalo, plusDney(r.nachalo, dlit));
    } else {
      const nach = r.nach_vr || r.nachalo;
      if (!nach) return;
      const iso = plusDney(nach, dney);
      const ok = await sohranit(r, { nachalo_vruchnuyu: iso },
        'Ручное начало «' + r.rabota + '» эт. ' + r.etazh + ': ' + fmt(iso));
      if (ok) zhdatPolosu(r.id, iso, plusDney(iso, Math.max(1, r.dlit || 1)));
    }
  }

  /** Записать перетаскивание суммарной полосы (общее для старого Ганта и холста).
   *  `a`, `b` — дни полосы до перетаскивания; `novoe` — новый день: у правого края —
   *  день после окончания, у всей полосы — начало. */
  async function tyanulGruppu(spisok: Row[], kluch: string, a: number, b: number, kray: boolean,
    novoe: number) {
    if (kray) {                     // правый край: меняем длительности
      const konec = novoe;
      const bylo = b + 1 - a, stalo = konec - a;
      if (bylo > 0 && stalo > 0 && stalo !== bylo) {
        const ok = await rastyanut(spisok, stalo / bylo);
        // даты пересчитывает «Актуализировать», поэтому до пересчёта показываем
        // полосу в новой длине пунктиром — как у обычных задач
        // начало, если группу уже сдвигали, сохраняем: иначе после растягивания
        // полоса возвращалась на исходное место (замечание владельца 2026-09-09)
        if (ok) setZhdutSum(z2 => ({ ...z2, [kluch]: { n: z2[kluch]?.n, o: dnyaISO(konec - 1) } }));
      }
      return;
    }
    // вся полоса (владелец 2026-09-15, вариант А): новое начало группы одной
    // датой «не раньше» на её задачи; дальше их раскладывает расчёт
    const nach = novoe;
    if (nach !== a) {
      const ok = await sdvinutGruppu(spisok, dnyaISO(nach));
      if (ok) setZhdutSum(z2 => ({ ...z2, [kluch]: { n: dnyaISO(nach), o: dnyaISO(b + nach - a) } }));
    }
  }

  /** дата ISO плюс сколько-то дней */
  const plusDney = (iso: string, n: number) => {
    const d = new Date(iso + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  };
  const zhdatPolosu = (id: number, n: string, o: string) => setZhdut(z => ({ ...z, [id]: { n, o } }));

  /** день (серийный номер) → дата ISO */
  const dnyaISO = (d: number) => new Date(d * D1).toISOString().slice(0, 10);

  /** растянуть группу: длительности незавершённых задач меняются пропорционально */
  async function rastyanut(spisok: Row[], k: number) {
    const ids = spisok.map(r => r.id);
    const r = await call<{ izmeneno: number; propuscheno: number }>('post',
      p('/api/projects/{pid}/tasks/masshtab', { pid: ctx.pid }), { ids, k });
    if (!r.ok) { fail(r.error.message); return false; }
    saved('Длительность изменена у задач: ' + r.data.izmeneno
      + (r.data.propuscheno ? '; законченных не тронуто: ' + r.data.propuscheno : '')
      + '. Нажмите «Пересчитать», чтобы обновить даты.');
    void load();
    return true;
  }

  /** сдвинуть группу (владелец 2026-09-15, вариант А): всем задачам без факта и ручных
      дат одна дата «не раньше» — новое начало группы; дальше их раскладывает расчёт */
  async function sdvinutGruppu(spisok: Row[], nachalo: string) {
    const ids = spisok.map(x => x.id);
    const r = await call<{ izmeneno: number; propuscheno: number }>('post',
      p('/api/projects/{pid}/tasks/sdvig', { pid: ctx.pid }), { ids, nachalo });
    if (!r.ok) { fail(r.error.message); return false; }
    if (!r.data.izmeneno) {
      fail('Двигать нечего: у всех задач группы факт или ручные даты');
      return false;
    }
    saved('Группа начнётся не раньше ' + fmt(nachalo) + ': дату получили задачи: ' + r.data.izmeneno
      + (r.data.propuscheno ? '; с фактом и ручными датами не тронуто: ' + r.data.propuscheno : '')
      + '. Нажмите «Пересчитать», чтобы пересчитать даты.');
    void load();
    return true;
  }

  /** сбросить корректировки группы (двойной клик по суммарной полосе) */
  async function sbrositSdvig(spisok: Row[]) {
    // владелец 2026-09-15: сброс снимает всё, что сделала корректировка суммарной
    // (декоративные «не раньше» и длительность), а ручные — отдельным согласием
    const dekor = spisok.filter(x => x.ogr || x.dd != null).length;
    const ruch = spisok.filter(x => x.dlit_r != null || x.nach_vr || x.okonch_vr).length;
    if (!dekor && !ruch) { saved('У этой группы корректировок нет — даты уже по модели.'); return; }
    if (dekor && !await confirmBox('Сбросить корректировки суммарной у этой группы?', {
      podpis: 'Декоративные сроки («не раньше» и растянутая длительность) уберутся у задач: '
        + dekor + '. Отменить можно стрелкой «назад».', ok: 'Сбросить' })) return;
    const ruchnye = ruch > 0 && await confirmBox('Сбросить и ручные корректировки?', {
      podpis: 'У задач группы: ' + ruch + ' — ручная длительность, начало или окончание. Их ставят '
        + 'в таблице и карточке задачи, раньше их ставило и растягивание полосы (до 13.09). '
        + 'Отменить можно стрелкой «назад».', ok: 'Сбросить и ручные', opasno: true });
    if (!dekor && !ruchnye) return;
    const ids = spisok.map(x => x.id);
    const r = await call<{ izmeneno: number }>('post',
      p('/api/projects/{pid}/tasks/sdvig', { pid: ctx.pid }), { ids, sbros: true, ruchnye });
    if (!r.ok) { fail(r.error.message); return; }
    saved('Корректировки сброшены у задач: ' + r.data.izmeneno
      + '. Нажмите «Пересчитать», чтобы пересчитать даты.');
    void load();
  }

  /** записать правку задачи с полосы Ганта */
  async function sohranit(row: Row, telo: Record<string, unknown>, soobshchenie: string) {
    const r = await call<{ rev: number }>('patch', p('/api/tasks/{tid}', { tid: row.id }),
      { rev: row.rev, ...telo });
    if (!r.ok) { fail(r.error.message); return false; }
    saved(soobshchenie.replace(/\.$/, '') + '. Полоса показана пунктиром: нажмите «Пересчитать», чтобы пересчитать даты.');
    void load();
    return true;
  }

  /** ячейки правой части: полоса Ганта или колонки-месяцы сводной */
  const vizYacheyki = (n?: string | null, o?: string | null, spisok?: Row[], vidPolosy?: string,
    row?: Row) => {
    if (holstOn) {
      // полосу рисует холст (GantHolst) поверх: здесь только место нужной ширины
      return (
        <td className="viz-c" style={{ padding: 0 }} onClick={e => e.stopPropagation()}>
          <div style={{ width: shirina, height: 1 }} />
        </td>
      );
    }
    if (vid === 'gant') {
      const zh = row ? zhdut[row.id] : undefined;
      const kluchSum0 = spisok ? (n || '') + '¶' + (o || '') + '¶' + spisok.length : '';
      const zhSum = spisok ? zhdutSum[kluchSum0] : undefined;
      const g = polosa(zh ? zh.n : (zhSum?.n || n), zh ? zh.o : (zhSum?.o || o));
      const s2 = row && sdvig && sdvig.id === row.id ? sdvig : null;
      // ключ суммарной строки — по её границам и числу задач
      const kluchSum = spisok ? (n || '') + '¶' + (o || '') + '¶' + spisok.length : '';
      const ss = spisok && sdvigSum && sdvigSum.kluch === kluchSum ? sdvigSum : null;
      let left = g ? g.left : 0;
      let width = g ? g.width : 0;
      if (g && s2) {                       // тянем задачу: считаем по целым дням
        const a2 = s2.kray ? g.a : g.a + s2.dney;
        const b2 = g.b + s2.dney;
        left = xDnya(a2);
        width = Math.max(3, xDnya(b2 + 1) - left);
      }
      if (g && ss) {                       // тянем суммарную: за край — длина, за полосу — вся группа
        if (ss.kray) {
          width = Math.max(3, xDnya(g.b + 1 + ss.dney) - g.left);
        } else {
          left = xDnya(g.a + ss.dney);
          width = Math.max(3, xDnya(g.b + 1 + ss.dney) - left);
        }
      }
      // полоса задачи ставит ручные сроки (владелец 2026-09-15): тянется в Ганте, щелчок
      // (сдвиг меньше POROG) ничего не пишет, подтверждения нет — отмена стрелкой
      const tyanem = !!row && can && summ && vid === 'gant' && !raschet;
      const tyanemSum = !!spisok && can && summ && vid === 'gant' && !!g && !raschet;
      return (
        <td className="viz-c" style={{ padding: 0 }} onClick={e => e.stopPropagation()}>
          <div className="viz-pole" style={{ width: shirina }}>
            {mesyacy.map((m, i) => <i key={m.k} className="viz-set" style={{ left: i * MW }} />)}
            {g && <div className={'viz-bar ' + (vidPolosy || '') + (raschet ? ' raschet' : '')
              + ((row ? critical(row) : spisok?.some(critical)) ? ' krit-polosa' : '')
              + (tyanem ? ' tyanem' : '')
              + (tyanemSum ? ' tyanem sum-drag' : '') + (zh || zhSum ? ' zhdet' : '')}
              style={{ left, width }}
              title={raschet ? fmt(n) + ' — ' + fmt(o) + '  ·  расчётный график: сроки по одной модели, без рамок'
                : zh ? fmt(zh.n) + ' — ' + fmt(zh.o) + '  ·  ждёт пересчёта: нажмите «Пересчитать»'
                : tyanemSum ? fmt(n) + ' — ' + fmt(o)
                  + '  ·  тяните полосу — группа начнётся не раньше новой даты (одна дата на все '
                  + 'задачи); за правый край — длительности незавершённых задач; двойной клик — '
                  + 'сбросить корректировки группы'
                : tyanem ? fmt(n) + ' — ' + fmt(o) + '  ·  тяните полосу — ручное начало, за правый край — '
                  + 'ручная длительность; отменить — стрелкой «назад»'
                : fmt(n) + ' — ' + fmt(o)}
              onClick={e => { e.stopPropagation(); }}
              onDoubleClick={e => {
                if (!tyanemSum || !spisok) return;
                e.preventDefault(); e.stopPropagation();
                void sbrositSdvig(spisok);
              }}
              onMouseDown={e => {
                if (tyanemSum && spisok) {
                  // суммарная (владелец 2026-09-15): правый край — длительности,
                  // середина — одна дата «не раньше» на всю группу (вариант А)
                  const box3 = (e.target as HTMLElement).getBoundingClientRect();
                  e.preventDefault(); e.stopPropagation();
                  tashimSum.current = { x0: e.clientX, a: g.a, b: g.b, spisok,
                                        kray: box3.right - e.clientX <= 9,
                                        kluch: (n || '') + '¶' + (o || '') + '¶' + spisok.length };
                  return;
                }
                if (!tyanem || !row) return;
                e.preventDefault(); e.stopPropagation();
                const box2 = (e.target as HTMLElement).getBoundingClientRect();
                tashim.current = { id: row.id, x0: e.clientX, kray: box2.right - e.clientX <= 7,
                                  row, a: g.a, b: g.b };
              }} />}
          </div>
        </td>
      );
    }
    return (
      <>
        {mesyacy.map(m => {
          const k = spisok ? vMesyace(spisok, m)
            : (o && dnum(o)! >= m.d0 && dnum(o)! <= m.d1 ? 1 : 0);
          return <td key={m.k} className={'num viz-m' + (k ? ' est' : '')}
            onClick={e => e.stopPropagation()}>{k || ''}</td>;
        })}
      </>
    );
  };

  // hOn обязан равняться числу колонок HAND (#52, 2026-09-13: было 4 при пяти колонках —
  // ячейки строк этапов и работ съезжали, и раскраска блоков сбивалась)
  const hOn = cols.hand ? HAND.length : 0, rOn = cols.res ? 4 : 0, fOn = cols.fakt ? 4 : 0;
  // столбец «Ошибка» — кратко, что не так (владелец 2026-09-16); вне списка HEAD, потому что
  // номер столбца HEAD — это уровень сортировки (SORT_GET); в «Ручном вводе» не показываем:
  // строка добавления разложена по столбцам таблицы
  const stolbecOsh = oshibki && !ruch && !ierarh;
  const sOn = (cols.sprav ? 2 : 0) + (cols.base ? 2 : 0);
  void sOn;
  // ячейки-заглушки строк этапов и работ — в цветах своих блоков, чтобы полосы блоков
  // шли через всю таблицу (замечание владельца #52, 2026-09-13)
  const zaglushki = (
    <>
      {ruch && <td className="col-ruch-pred"></td>}
      {hOn > 0 && <td className="col-hand" colSpan={hOn}></td>}
      {rOn > 0 && <td className="col-res" colSpan={rOn}></td>}
      <td className="col-pred" colSpan={ruch ? 5 : 6}></td>
      {fOn > 0 && <td className="col-fakt" colSpan={fOn}></td>}
      {cols.sprav && <td className="col-sprav" colSpan={2}></td>}
      {cols.base && <td className="col-base" colSpan={2}></td>}
      {kritColumn && <td className="col-krit" title="У суммарной строки нет собственного запаса; выделены критические задачи внутри"></td>}
    </>
  );
  const spanTail = (grouped: boolean) => 7 + (grouped ? 0 : 0);
  void spanTail;

  async function patchTask(row: Row, key: string, val: string | null, col: Col) {
    const r = await call<Record<string, unknown>>('patch', p('/api/tasks/{tid}', { tid: row.id }), { rev: row.rev, [key]: val });
    if (!r.ok) {
      fail(r.error.message);
      if (r.error.code === 'stale') void load();
      return;
    }
    col.apply(row, val, r.data);
    if (typeof r.data.rev === 'number') row.rev = r.data.rev;
    saved();
    setRows(rs => rs.slice());
  }

  /** ручная дата начала/окончания из ячейки «Расчёта» — сохранить и сразу пересчитать сроки (2026-10-18) */
  async function patchSrok(row: Row, key: string, val: string | null, col: Col) {
    const r = await call<Record<string, unknown>>('patch', p('/api/tasks/{tid}', { tid: row.id }), { rev: row.rev, [key]: val });
    if (!r.ok) { fail(r.error.message); if (r.error.code === 'stale') void load(); return; }
    col.apply(row, val, r.data);
    if (typeof r.data.rev === 'number') row.rev = r.data.rev;
    setRows(rs => rs.slice());
    const rc = await call<{ aktualizirovano: string | null }>('post', p('/api/projects/{pid}/recalc', { pid: ctx.pid }), {});
    if (!rc.ok) { fail('Дата сохранена, но пересчёт не прошёл: ' + rc.error.message); return; }
    saved((key === 'dlit_ruchnaya' ? (val ? 'Длительность задана вручную: ' + val + ' дн.' : 'Ручная длительность снята')
      : val ? (key === 'nachalo_vruchnuyu' ? 'Начало' : 'Окончание') + ' задано вручную: ' + fmt(val) : 'Ручная дата снята')
      + ' — сроки пересчитаны.');
    ctx.onAfterRecalc?.(rc.data.aktualizirovano);
    void load();
  }

  function clickRow(e: React.MouseEvent, row: Row) {
    if (!e.ctrlKey && !e.metaKey && !e.shiftKey) { if (ruch) setTek({ tip: 'task', id: row.id }); return; }
    e.preventDefault(); e.stopPropagation();
    const next = new Set(sel);
    if (e.shiftKey && selLast != null) {
      const vis = display.filter(d => d.kind === 'task').map(d => (d as { row: Row }).row.id);
      const a = vis.indexOf(selLast), b = vis.indexOf(row.id);
      if (a >= 0 && b >= 0) vis.slice(Math.min(a, b), Math.max(a, b) + 1).forEach(x => next.add(x));
    } else if (next.has(row.id)) next.delete(row.id); else next.add(row.id);
    setSel(next); setSelLast(row.id);
  }
  // Esc снимает выделение строк (панель «Выбрано: N»; владелец 2026-10-27) — если курсор не в поле и не открыто окно
  useEffect(() => {
    if (!sel.size) return;
    const f = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (document.querySelector('.dlg')) return;
      setSel(new Set()); setSelLast(null);
    };
    document.addEventListener('keydown', f);
    return () => document.removeEventListener('keydown', f);
  }, [sel.size]);

  // ---------- дерево ручного планирования (#126): клавиши, строка ввода, группы ----------
  async function sdvigUzla(u: Uzel, kuda: 'vpravo' | 'vlevo' | 'vverh' | 'vniz') {
    const r = await call<{ parent_id: number | null; gruppa_id: number | null }>('post',
      p('/api/projects/{pid}/ruch/sdvig', { pid: ctx.pid }), { ...u, kuda });
    if (!r.ok) { fail(r.error.message); return; }
    if (r.data.parent_id != null) setRSvern(s0 => { const n = new Set(s0); n.delete(r.data.parent_id!); return n; });
    if (r.data.gruppa_id) saved('Строка выше стала группой — в неё вложена выбранная.');
    await load();
  }
  /** строка дерева по строке таблицы: задача или группа; остальное не связывается */
  const uzelStroki = (d?: Display): Uzel | null => (d?.kind === 'task' ? { tip: 'task', id: d.row.id }
    : d?.kind === 'rgr' ? { tip: 'gruppa', id: d.id } : null);
  const imyaUzla = (u: Uzel) => (u.tip === 'gruppa' ? 'группа «' + (rGruppy.find(g => g.id === u.id)?.name || '?') + '»'
    : (() => { const r = rows.find(x => x.id === u.id); return r ? '№ ' + (r.nomer ?? '') + ' ' + r.rabota : 'задача'; })());
  /** связь «окончание → начало» (#127): протянули полосу на Ганте вверх или вниз на другую */
  async function svyazat(ot: Uzel, k: Uzel) {
    const r = await call<{ sozdana: boolean }>('post', p('/api/projects/{pid}/ruch/svyaz', { pid: ctx.pid }), { ot, k });
    if (!r.ok) { fail(r.error.message); return; }
    saved(r.data.sozdana ? 'Связь: ' + imyaUzla(ot) + ' → ' + imyaUzla(k) + '. Даты — после «Пересчитать».'
      : 'Такая связь уже есть.');
    void load();
  }
  /** «Связать цепочкой»: выбранные задачи в порядке таблицы, каждая следующая — после предыдущей */
  async function cepochkoj() {
    const uzly = display.filter(d => d.kind === 'task' && sel.has(d.row.id)).map(d => uzelStroki(d)!);
    const r = await call<{ sozdano: number }>('post', p('/api/projects/{pid}/ruch/cepochkoj', { pid: ctx.pid }), { uzly });
    if (!r.ok) { fail(r.error.message); return; }
    saved('Связано цепочкой: ' + uzly.length + ' строк, новых связей ' + r.data.sozdano + '. Даты — после «Пересчитать».');
    void load();
  }
  /** размножить группу (#128): «Захватка 1» → «Захватка 2…» с теми же работами, днями и связями внутри */
  async function razmnozhit(gid: number, imya: string) {
    const v = await ask('Сколько копий группы «' + imya + '» сделать?', '1', {
      ok: 'Размножить',
      podpis: 'Копии встанут сразу после неё с продолжением номера («Захватка 2», «Захватка 3»…): те же работы, этажи, '
        + 'дни и связи внутри группы; место с тем же номером тоже сменится. Ручные даты, факт и связи с другими строками '
        + 'не копируются. Отмена — стрелкой ↺.' });
    if (!v) return;
    const n = parseInt(v, 10);
    if (!(n >= 1 && n <= 50)) { fail('Число копий — от 1 до 50'); return; }
    const r = await call<{ grupp: number; zadach: number; imena: string[] }>('post',
      p('/api/projects/{pid}/ruch/razmnozhit', { pid: ctx.pid }), { gruppa_id: gid, n });
    if (!r.ok) { fail(r.error.message); return; }
    saved('Готово: ' + r.data.imena.join(', ') + ' (задач: ' + r.data.zadach + '). Вернуть — стрелкой ↺. Даты — после «Пересчитать».');
    void load();
  }
  async function udalitSvyaz(sid: number) {
    const r = await call('delete', p('/api/projects/{pid}/ruch/svyazi/{sid}', { pid: ctx.pid, sid }));
    if (!r.ok) { fail(r.error.message); return; }
    saved('Связь удалена. Даты — после «Пересчитать».');
    void load();
  }
  async function udalitVybrannoe() {
    const ids = sel.size ? [...sel] : tek?.tip === 'task' ? [tek.id] : [];
    const gruppy = !sel.size && tek?.tip === 'gruppa' ? [tek.id] : [];
    if (!ids.length && !gruppy.length) return;
    const r = await call<{ udaleno: number; propuscheno: number; grupp: number }>('post',
      p('/api/projects/{pid}/tasks/udalit', { pid: ctx.pid }), { ids, gruppy });
    if (!r.ok) { fail(r.error.message); return; }
    saved((r.data.grupp ? 'Удалена группа' + (r.data.udaleno ? ' и задач: ' + r.data.udaleno : '') : 'Удалено задач: ' + r.data.udaleno)
      + '. Вернуть — стрелкой ↺.');
    setSel(new Set()); setTek(null);
    void load();
  }
  async function pereimenovat(gid: number, imya: string) {
    const v = await ask('Название группы', imya);
    if (!v || v === imya) return;
    const r = await call('patch', p('/api/projects/{pid}/ruch-gruppy/{gid}', { pid: ctx.pid, gid }), { name: v });
    if (!r.ok) { fail(r.error.message); return; }
    void load();
  }
  /** Tab в строке ввода: набрано название — новая группа, курсор внутрь; пусто — строка ввода уходит
   *  в строку выше (задача выше становится группой, как в Project) */
  async function vvodTab(imya: string) {
    if (imya) {
      const r = await call<{ id: number }>('post', p('/api/projects/{pid}/ruch-gruppy', { pid: ctx.pid }),
        { name: imya, parent_id: vvod.parent, posle: vvod.posle });
      if (!r.ok) { fail(r.error.message); return false; }
      await load();
      setVvod({ parent: r.data.id, posle: null });
      return true;
    }
    const ps = vvod.posle;
    if (!ps || ps === 'nachalo') { fail('Выше на этом уровне нет строки, в которую можно вложить'); return false; }
    if (ps.tip === 'gruppa') { setVvod({ parent: ps.id, posle: null }); return true; }
    const r = await call<{ gruppa_id: number }>('post', p('/api/projects/{pid}/ruch/v-gruppu', { pid: ctx.pid }), { task_id: ps.id });
    if (!r.ok) { fail(r.error.message); return false; }
    await load();
    setVvod({ parent: r.data.gruppa_id, posle: null });
    return true;
  }
  /** Shift+Tab в строке ввода: на уровень выше, сразу после своей группы */
  function vvodShiftTab() {
    if (vvod.parent == null) return;
    setVvod({ parent: rgRoditel.get(vvod.parent) ?? null, posle: { tip: 'gruppa', id: vvod.parent } });
  }
  // клавиши у выбранной строки дерева: Tab / Shift+Tab, Alt+↑/↓, Delete, Enter (строка ввода ниже), F2
  useEffect(() => {
    if (!ruch || !can) return;
    const f = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      // выделена ячейка (дата, длительность, место…) — клавиши её: Delete очищает значение, а не удаляет строку
      // (владелец 2026-10-27: «выбрал дату, нажал удалить — удалилась вся задача»)
      if (e.defaultPrevented || (t && t.closest('td.edit'))) return;
      if (document.querySelector('.dlg')) return;   // открыт диалог
      if (e.key === 'Delete' && (sel.size || tek)) { e.preventDefault(); void udalitVybrannoe(); return; }
      // Ctrl+F2 — связать выделенные задачи цепочкой, как в MS Project
      if (e.key === 'F2' && (e.ctrlKey || e.metaKey) && sel.size > 1) { e.preventDefault(); void cepochkoj(); return; }
      if (!tek) return;
      if (e.key === 'Tab') { e.preventDefault(); void sdvigUzla(tek, e.shiftKey ? 'vlevo' : 'vpravo'); }
      else if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) { e.preventDefault(); void sdvigUzla(tek, e.key === 'ArrowUp' ? 'vverh' : 'vniz'); }
      else if (e.key === 'Enter') { e.preventDefault(); setVvod({ parent: roditel(tek), posle: tek }); }
      else if ((e.ctrlKey || e.metaKey) && (e.key === 'd' || e.key === 'в' || e.code === 'KeyD') && tek.tip === 'gruppa') {
        e.preventDefault(); void razmnozhit(tek.id, rGruppy.find(g => g.id === tek.id)?.name || '');
      }
      else if (e.key === 'F2' && tek.tip === 'gruppa') { e.preventDefault(); void pereimenovat(tek.id, rGruppy.find(g => g.id === tek.id)?.name || ''); }
      else if (e.key === 'Escape') setTek(null);
    };
    document.addEventListener('keydown', f);
    return () => document.removeEventListener('keydown', f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruch, can, tek, sel, rGruppy, rows, vvod]);

  function clickWorkGroup(e: React.MouseEvent, name: string) {
    if (e.ctrlKey || e.metaKey || e.shiftKey) {
      e.preventDefault(); e.stopPropagation();
      const ids = rows.filter(x => x.rabota === name).map(x => x.id);
      const all = ids.length && ids.every(id => sel.has(id));
      const next = new Set(sel);
      ids.forEach(id => { if (all) next.delete(id); else next.add(id); });
      setSel(next);
      return;
    }
    setOpenWorks(s => { const n = new Set(s); if (n.has(name)) n.delete(name); else n.add(name); return n; });
  }

  const headClick = (i: number) => {
    if (!SORT_GET[i]) return;
    setSort(s => {
      const j = s.findIndex(x => x.i === i);
      if (j < 0) return [...s, { i, dir: 1 as const }];        // новый уровень в конец
      if (s[j].dir === 1) return s.map((x, k) => (k === j ? { i, dir: -1 as const } : x));
      return s.filter((_, k) => k !== j);                      // третий клик — снять
    });
  };
  /** стрелка направления и номер уровня, если сортировка не одна */
  const arrow = (i: number) => {
    const j = sort.findIndex(x => x.i === i);
    if (j < 0) return '';
    return (sort[j].dir === 1 ? ' ▲' : ' ▼') + (sort.length > 1 ? String(j + 1) : '');
  };

  // ---- холст (прототип 2026-09-18): полосы по строкам display и место задачи для стрелок ----
  const poEtapu = useMemo(() => {   // ключ раздела (путь) → задачи под ним на любой глубине
    const m = new Map<string, Row[]>();
    rows.forEach(r => {
      const put = putOf(r);
      put.forEach((_, i) => { const k = klyuchPuti(put, i); (m.get(k) || m.set(k, []).get(k)!).push(r); });
    });
    return m;
  }, [rows, putOf]);
  /** задачи строки ГПР — все задачи её работ (#120) */
  const zadachiStroki = (imya: string) => rows.filter(r => strokaRab.get(r.rabota) === imya);
  /** задачи суммарной строки: раздел, строка ГПР или работа */
  const spisokGruppy = (d: Display) => (d.kind === 'etap' ? poEtapu.get(d.key) || []
    : d.kind === 'rgr' ? zadachiGruppy(d.id)
    : d.kind === 'work' ? poRabote.get(d.name) || []
    : d.kind === 'stroka' ? zadachiStroki(d.name)
    : d.kind === 'ier' ? ierSroki.get(d.key)?.spisok || [] : []);
  const holstPolosy = useMemo(() => {
    if (!holstOn) return [] as (HPolosa | null)[];
    return display.map((d): HPolosa | null => {
      if (d.kind === 'vvod') return null;
      if (d.kind === 'task') {
        const r = d.row, zh = zhdut[r.id];
        const n = zh ? zh.n : r.nachalo, o = zh ? zh.o : r.okonchanie;
        if (!n || !o) return null;
        const done = r.sv === 'СМР окончены' || r.sv === 'СМР приняты';
        const fiks = !!r.ruchnoe || !!r.sv || !!r.vn || !!r.vo;
        return { a: dnum(n)!, b: dnum(o)!, vid: 'zad', zhdet: !!zh, proc: r.proc ?? null,
          krit: !zh && critical(r),
          sost: done ? 'done' : r.sv === 'СМР начаты' ? 'act' : '',
          cvet: problemy.has(r.id) ? 'oshibka' : done ? 'done' : fiks ? 'fiks' : '',
          podpis: '№ ' + (r.nomer ?? '—') + ' · ' + r.rabota + ' · ' + (r.korpus || planKorpus)
            + (r.etazh != null ? ', эт. ' + r.etazh : '') + '\n' + fmt(n) + ' — ' + fmt(o)
            + (r.dlit != null ? ' · ' + r.dlit + ' дн.' : '') + (r.sv ? ' · ' + r.sv : '')
            + (r.proc != null ? ' ' + r.proc + '%' : '')
            + (kritMap.has(r.id) ? '\nЗапас: ' + (kritMap.get(r.id)!.zapas_dnej ?? 'не применяется')
              + (critical(r) ? ' · критический путь' : '') : '')
            + (zh ? '\nЖдёт пересчёта: нажмите «Пересчитать»' : '') };
      }
      const spisok = spisokGruppy(d);
      const zh = zhdutSum[(d.min || '') + '¶' + (d.max || '') + '¶' + spisok.length];
      const n = zh?.n || d.min, o = zh?.o || d.max;
      if (!n || !o) return null;
      const razdel = d.kind === 'etap' || d.kind === 'rgr' || (d.kind === 'ier' && d.razdel);
      return { a: dnum(n)!, b: dnum(o)!, vid: razdel ? 'etap' : 'rab', zhdet: !!zh, proc: null, sost: '',
        krit: !zh && spisok.some(critical),
        podpis: (d.kind === 'rgr' ? 'Группа «' : razdel ? 'Этап «' : d.kind === 'stroka' ? 'Строка ГПР «' : 'Работа «') + d.name + '» · задач: ' + spisok.length
          + '\n' + fmt(n) + ' — ' + fmt(o) + (zh ? '\nЖдёт пересчёта: нажмите «Пересчитать»' : '') };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holstOn, display, zhdut, zhdutSum, poEtapu, poRabote, planKorpus, problemy, rgRoditel, kritMap]);
  /** где на экране задача: своя строка, а если свёрнута — строка работы или этапа */
  const gdeZadacha = useMemo(() => {
    const m = new Map<number, number>();
    if (!holstOn) return m;
    const rab = new Map<string, number>(), etp = new Map<string, number>(), str = new Map<string, number>();
    const grp = new Map<number, number>();
    display.forEach((d, i) => {
      if (d.kind === 'vvod') return;
      if (d.kind === 'rgr') { grp.set(d.id, i); return; }
      if (d.kind === 'task') m.set(d.row.id, i);
      else if (d.kind === 'work') rab.set(d.name, i);
      else if (d.kind === 'stroka') str.set(d.name, i);
      else if (d.kind === 'ier') { if (d.razdel) etp.set(d.key, i); else rab.set(d.name, i); }
      else etp.set(d.key, i);
    });
    if (ruch) {     // свёрнутая группа дерева: задача ведётся к ближайшей показанной группе выше (#126)
      for (const r of rows) {
        if (m.has(r.id)) continue;
        for (let x: number | null = r.rg ?? null; x != null; x = rgRoditel.get(x) ?? null)
          if (grp.has(x)) { m.set(r.id, grp.get(x)!); break; }
      }
      return m;
    }
    if (ierarh) {   // свёрнутый раздел: задача ведётся к ближайшему показанному разделу выше
      for (const r of rows) {
        if (m.has(r.id)) continue;
        let i = rab.get(r.rabota);
        if (i == null) {
          const k = ie.rows.findIndex(x => x.name === r.rabota && !x.name.trim() === false);
          for (let g = k; g >= 0 && i == null; g--) if (ie.rows[g].glubina < (ie.rows[k]?.glubina ?? 0)) i = etp.get(ie.rows[g].klyuch);
        }
        if (i != null) m.set(r.id, i);
      }
      return m;
    }
    for (const r of rows) {
      if (m.has(r.id)) continue;
      let i = rab.get(r.rabota);
      if (i == null && strokaRab.has(r.rabota)) i = str.get(strokaRab.get(r.rabota)!);   // свёрнута строка ГПР
      if (i == null) {   // свёрнуто: ближайший показанный раздел снизу вверх
        const put = putOf(r);
        for (let g = put.length - 1; g >= 0 && i == null; g--) i = etp.get(klyuchPuti(put, g));
      }
      if (i != null) m.set(r.id, i);
    }
    return m;
  }, [holstOn, display, rows, putOf, ierarh, ie.rows, strokaRab, ruch, rgRoditel]);
  const holstTyanul = (di: number, kray: boolean, dney: number, a: number, b: number) => {
    const d = display[di];
    if (!d || d.kind === 'vvod') return;
    if (d.kind === 'task') { void tyanulZadachu(d.row, kray, dney); return; }
    const spisok = spisokGruppy(d);
    void tyanulGruppu(spisok, (d.min || '') + '¶' + (d.max || '') + '¶' + spisok.length, a, b, kray,
      kray ? b + 1 + dney : a + dney);
  };
  const holstDvojnoj = (di: number) => {
    const d = display[di];
    if (!d || d.kind === 'vvod') return;
    if (d.kind === 'task') setPop({ kind: 'task', row: d.row, vkladka: 'obzor' });
    else if (d.kind === 'ier') { /* в иерархии двойной клик по полосе ничего не сбрасывает */ }
    else if (can && !raschet) void sbrositSdvig(spisokGruppy(d));
  };

  // столбец «Секц.» (он же захватка) обычно скрыт; показываем, как только у задач
  // появились секции (#70, 2026-09-18) — иначе планер не видит, где А, а где Б
  const estSek = rows.some(r => r.sek);
  // «Планирование» по эскизу (владелец 2026-10-17): столбцов «Корп.», «Эт.», «Секц.» нет — корпус, этаж
  // и секция дописаны в строку задачи; освободившееся место — названию. Ручной режим и иерархия — как было
  const bezKE = !ruch && !ierarh;
  const tableCls = 'hide-prior hide-brig' + (estSek || ruch ? '' : ' hide-zahv') + (bezKE ? ' bez-ke' : '') + (ruch ? ' ruch-rezh' : '')
    + (cols.hand && !ierarh ? '' : ' hide-hand') + (cols.pred && !ierarh ? '' : ' hide-pred')
    + (cols.sprav && !ierarh ? '' : ' hide-sprav') + (cols.fakt && !ierarh ? '' : ' hide-fakt') + (cols.base && !ierarh ? '' : ' hide-base')
    + (cols.res && !ierarh ? '' : ' hide-res') + (ierarh ? ' ierarh hide-itog' : '');

  return (
    <div>
      {!ierarh && !raschet && <div className="bar krit-bar">
        <button className={'sec' + (kritOn ? ' on' : '')} aria-pressed={kritOn}
          title="Запас до окончания всего проекта с учётом связей и очередей бригад; фильтры не меняют расчёт"
          onClick={() => setKritOn(v => !v)}>Критический путь</button>
        {kritOn && <>
          <label><input type="checkbox" checked={tolkoKrit} disabled={!kritMap.size}
            onChange={e => setTolkoKrit(e.target.checked)} /> Только критические</label>
          <span className="muted" title={kritResult?.preduprezhdeniya.join('\n') || undefined}>
            {ctx.datyUstareli ? 'Данные изменились. Нажмите «Пересчитать».' : kritError || kritResult?.prichina || (!kritResult ? 'Считаем запас…'
              : kritResult.status === 'ok' && !kritMap.size && data?.rows.some(r => !r.iskl)
              ? 'Даты изменились. Нажмите «Пересчитать».'
              : 'До окончания проекта ' + fmt(kritResult.okonchanie) + '. Запас — календарные дни.')}
          </span>
          {!!kritResult?.preduprezhdeniya.length && <span className="muted" title={kritResult.preduprezhdeniya.join('\n')}>ⓘ Особенности связей</span>}
        </>}
      </div>}
      {/* в ручном панели выбора нет (владелец 2026-10-27: «убери, если что — потом добавим»): выделенные удаляет Delete,
          связывает цепочкой Ctrl+F2 (как в MS Project), снимает выделение Esc */}
      {sel.size > 0 && !ruch && <SelBar count={sel.size} podr={podr} can={can}
        ruchnyh={[...sel].filter(id => {
          const r = rows.find(x => x.id === id);
          return !!r && ruchnayaRabota(r.rabota);
        }).length}
        sFaktom={rows.filter(r => sel.has(r.id) && estFakt(r)).length}
        onClearFakt={() => ochistitFakt([...sel])}
        onClear={() => { setSel(new Set()); setSelLast(null); }}
        onLinks={e => setPop({ kind: 'bulklinks', at: at(e) })}
        onCepochka={ruch && sel.size > 1 ? () => void cepochkoj() : undefined}
        onDelete={async () => {
          const r = await call<{ udaleno: number; propuscheno: number }>('post',
            p('/api/projects/{pid}/tasks/udalit', { pid: ctx.pid }), { ids: [...sel] });
          if (!r.ok) { fail(r.error.message); return; }
          saved('Удалено задач: ' + r.data.udaleno
            + (r.data.propuscheno ? ' (раскатанные не удаляются, пропущено: ' + r.data.propuscheno + ')' : '')
            + '. Вернуть — стрелкой ↺. Нажмите «Пересчитать», чтобы обновить даты.');
          setSel(new Set()); setSelLast(null);
          void load();
        }}
        onApply={async body => {
          const r = await call<{ izmeneno: number }>('post', p('/api/projects/{pid}/tasks/bulk', { pid: ctx.pid }),
            { ids: [...sel], ...body });
          if (!r.ok) { fail(r.error.message); return false; }
          saved('Записано задач: ' + r.data.izmeneno + '. Нажмите «Пересчитать», чтобы обновить даты.');
          setSel(new Set()); setSelLast(null);
          void load();
          return true;
        }} />}

      {/* position: relative обязателен (#50/#57, 2026-09-13): слой стрелок позиционируется
          от этого контейнера; без него он привязывался к странице, и при любом изменении
          высоты шапки или панели фильтров стрелки уезжали от полос */}
      {/* поэтажная шахматка (владелец 2026-09-15): «этаж × работа», дата окончания в клетке */}
      {vid === 'shahm' ? (
        <div style={panel ? { marginRight: detH + 12 } : undefined}>
          {/* поиск работы — в панели фильтров (2026-10-17) */}
          {/* клик по клетке — сведения о работе в нижней панели (владелец 2026-09-15);
              сетка уступает панели высоту так же, как таблица */}
          <ShahmatkaGPR rows={rows} struct={struct} meta={meta} boxRef={box}
            tekId={pop?.kind === 'task' ? pop.row.id : null}
            maxHeight={undefined}
            onOpen={row => setPop({ kind: 'task', row, vkladka: 'rabota' })} />
        </div>
      ) : (
      <>
      {ierarh && <div className="bar actbar ie-bar"><b>Иерархия работ</b><IerPodskazka ie={ie} /></div>}
      {/* пустой проект — ведём планера по сценарию «сначала этажи, потом работы» (владелец
          2026-10-15: завёл проект и не понял, как добавить работы в график — экран молчал,
          «показано 0 из 0 задач»). Таблица под подсказкой остаётся — на неё завязаны слои */}
      {data && !ierarh && Object.keys(struct).length === 0 && (
        <div className="pv-pusto">
          <b>График пуст: в проекте нет корпусов</b>
          <span>Задачи графика рождаются из «работа × корпус × этаж». Шаг 1 — заведите корпуса и
            этажи на экране «Этажи», шаг 2 — работы здесь, в режиме «Иерархия».</span>
          <button onClick={() => ctx.onGoTab?.('struct')}>Перейти к «Этажам»</button>
        </div>
      )}
      {data && !ierarh && Object.keys(struct).length > 0 && Object.keys(meta).length === 0 && (
        <div className="pv-pusto">
          <b>Корпуса есть, работ пока нет</b>
          <span>Шаг 2 — наберите структуру работ: разделы и работы прямо в таблице, как в Project
            (Enter — следующая строка, Tab — вложить). Каждая работа сразу разворачивается в задачи
            по этажам.</span>
          {can && <button onClick={() => onIerarh?.()}>Включить «Иерархию»</button>}
        </div>
      )}
      <div className="tbl" ref={box}
        style={{ position: 'relative',
          // панель деталей забирает высоту у таблицы: окно прокрутки меряется
          // от контейнера, поэтому виртуализация подстроится сама
          // панель деталей — справа (2026-10-18): таблица уступает ей ширину
          ...(panel ? { marginRight: detH + 12 } : null) }}
        onScroll={e => setScroll((e.target as HTMLDivElement).scrollTop)}>
        {/* стрелки связей рисуются поверх таблицы, координаты берутся у самих
            полос — так стрелка не разъезжается с полосой (2026-09-10) */}
        {ierarh && <SvyaziShema pid={ctx.pid} box={box} can={can} vybrana={vybrSv} onVybrana={setVybrSv}
          risuj={scroll + display.length + first + ie.rows.length + svod.length + (ie.svernuto.size * 7)}
          onChanged={() => { saved('Связь сохранена. Даты — после «Пересчитать».'); void load(); }} onFail={fail} />}
        {holstOn && <GantHolst box={box} polosy={holstPolosy} d0={hd0} d1={hd1} px={px} onPx={smenitPx} knopkiVne={onGant}
          pary={paryList} gde={gdeZadacha} tyanem={can && summ && !raschet} raschet={raschet}
          onTyanul={holstTyanul} onDvojnoj={holstDvojnoj}
          onSvyaz={ruch && can && !raschet ? (a, b) => {
            const ot = uzelStroki(display[a]), k = uzelStroki(display[b]);
            if (ot && k) void svyazat(ot, k);
          } : undefined}
          vybrana={pop?.kind === 'task' ? gdeZadacha.get(pop.row.id) : undefined} />}
        {svyazi && vid === 'gant' && !holstOn && <SvyaziSloj box={box} pary={paryList} rabotaZadachi={rabotaZadachi}
          risuj={scroll + display.length + first + openWorks.size + closedEtap.size
            + tableCls.length * 1000 + (raschet ? 7 : 0)} />}   {/* #50: блоки столбцов сдвигают полосы */}
        <table id="planTable" className={tableCls}>
          <thead>
            <tr className="gh">
              {/* фильтр по работе — над «Работой», рядом с «Расчётом» (владелец 2026-09-15) */}
              {/* поиск работы переехал в панель фильтров (2026-10-17, эскиз «Планирования») */}
              <th colSpan={bezKE ? 2 : estSek || ruch ? 5 : 4} className="fx-h" />
              <th colSpan={3} className="col-itog col-b">Расчёт</th>
              {ruch && <th className="col-ruch-pred" />}
              {/* четыре столбца: карточка, дни, раскатка, порядок этажей (2026-10-16) */}
              {ierarh && <th colSpan={4} className="col-ier">Структура</th>}
              {stolbecOsh && <th className="col-osh">Ошибка</th>}
              {/* блоки столбцов остаются при Ганте и сводной, визуализация встаёт
                  правее них (владелец, #47 2026-09-13; раньше Гант вытеснял блоки) */}
              {cols.hand && <th colSpan={HAND.length} className="col-hand">Сроки</th>}
              {cols.res && <th colSpan={4} className="col-res">Ресурс</th>}
              {cols.pred && <th colSpan={ruch ? 5 : 6} className="col-pred">Предшественники</th>}
              {cols.fakt && <th colSpan={4} className="col-fakt">Факт</th>}
              {cols.sprav && <th colSpan={2} className="col-sprav">Справочник</th>}
              {cols.base && <th colSpan={2} className="col-base">Базовый</th>}
              {kritColumn && <th className="col-krit" title="Критический путь с учётом очередей бригад">Запас</th>}
              {ierarh && <th className="viz-h sv-h" title="Типовые связи между работами: предшественник левее. Новая связь — потяните от кружка блока к работе, которая идёт следом; щелчок по подписи стрелки — тип, задержка, сдвиг этажей, мягкая, удалить">
                <span className="sv-h-txt">Связи — тяните от кружка к следующей работе, подпись стрелки — тип и задержка</span></th>}
              {viz && !ierarh && <th className="viz-h" colSpan={vid === 'gant' ? 1 : mesyacy.length}>
                {vid === 'gant' ? 'Гант' : 'Сводная: задач заканчивается в месяце'}</th>}
            </tr>
            <tr>
              {HEAD.map((h, i) => (
                <th key={h.t + i} className={h.c} title={ruch && h.c === 'col-zahv' ? 'Место: захватка, участок — любой текст' : h.ttl}
                  onClick={() => headClick(i)}>{(ruch && h.c === 'col-zahv' ? 'Место' : h.t) + arrow(i)}</th>
              )).flatMap((th, i) => (ruch && HEAD[i].t === 'Ручн. предш.' ? []
                : ruch && i === HEAD_ITOG_POSLEDNIJ
                ? [th, <th key="ruch-pred" className="col-ruch-pred"
                    title="Ручные предшественники задачи. Клик по ячейке — открыть карточку связей">Предшественник</th>]
                : ierarh && (i === 2 || i === 3) ? []
                : ierarh && i === 1
                ? [<th key="ie-k" className="col-korp ie-kontur-h" colSpan={estSek ? 3 : 2} style={{ width: estSek ? 150 : 80, maxWidth: estSek ? 150 : 80 }}
                    title="Контур планирования этапа: проставляется всем работам под ним и появляется кнопкой в фильтрах сверху">Контур</th>]
                : stolbecOsh && i === HEAD_ITOG_POSLEDNIJ
                ? [th, <th key="osh" className="col-osh w-osh"
                    title="Кратко, что не так с задачей: ● — ошибка, ○ — конфликт. Разбор и что делать — на экране «Проверки»">
                    Что не так</th>]
                : ierarh && i === HEAD_ITOG_POSLEDNIJ
                ? [th, <th key="ie-k" className="col-ier ie-kart" title="Карточка работы — остальные признаки справочника"></th>,
                   <th key="ie-d" className="col-ier ie-dlit" title="Длительность одной задачи работы, дней (справочник)">Дней</th>,
                   <th key="ie-r" className="col-ier ie-rask" title="Раскатка работы: как она превращается в задачи">Раскатка</th>,
                   <th key="ie-p" className="col-ier ie-por" title="Порядок этажей (логика монтажа): очерёдность бригады по этажам">Порядок этажей</th>]
                : [th]))}
              {kritColumn && <th className="col-krit" title="На сколько календарных дней можно отложить задачу без переноса окончания проекта; у закреплённых дат — при снятии фиксации">Запас, дн</th>}
              {ierarh && <th className="viz-c" style={{ padding: 0 }}><div style={{ width: svShirina }} /></th>}
              {viz && !ierarh && vid === 'gant' && holstOn && (
                <th className="viz-c" style={{ padding: 0 }}><div style={{ width: shirina }} /></th>
              )}
              {viz && !ierarh && vid === 'gant' && !holstOn && (
                <th className="viz-c" style={{ padding: 0 }}>
                  <div className="viz-shkala" style={{ width: shirina }}>
                    {mesyacy.map(m => <span key={m.k} style={{ width: MW }}>{m.label}</span>)}
                  </div>
                </th>
              )}
              {viz && !ierarh && vid === 'pivot' && mesyacy.map(m => (
                <th key={m.k} className="num viz-m">{m.label}</th>
              ))}
            </tr>
          </thead>
          {/* Ctrl/Shift+щелчок выделяет строки, а не текст (владелец 2026-10-27): браузер начинает выделение текста
              на mousedown — гасим его там; в полях ввода — как обычно */}
          <tbody onMouseDown={e => {
            if (!(e.shiftKey || e.ctrlKey || e.metaKey)) return;
            const t = e.target as HTMLElement;
            if (t.closest('input, select, textarea')) return;
            e.preventDefault();
            window.getSelection()?.removeAllRanges();
          }}>
            {padTop > 0 && <tr style={{ height: padTop }}><td colSpan={33} style={{ padding: 0, border: 0 }} /></tr>}
            {display.slice(first, last).map((d, i) => {
              if (d.kind === 'ier') {
                const r = ie.rows[d.i];
                const k = ie.kuda;
                const pusto = !r.name.trim();
                // выглядит как строка раздела/работы графика (владелец: «форматирование не должно
                // скакать»): те же плюс/минус в номере, отступы и жирность; поле ввода — вместо текста
                const svern = ie.svernuto.has(d.key);
                const pereklyuchit = () => { if (d.razdel) ie.svernut(d.key); };

                return (
                  <tr key={'i' + d.key} data-di={first + i} data-grp={d.razdel ? undefined : d.name}
                    className={'ie-str' + (d.razdel ? ' grp etp razdel' + (r.glubina ? ' etp-' + Math.min(r.glubina, 3) : '') : pusto ? ' pusto' : ' grp')
                      + (k && k.i === d.i ? (k.posle ? ' drop-posle' : ' drop-do') : '')}
                    style={{ height: ROW_H, ...(k && k.i === d.i ? { '--drop-otstup': (k.glubina * 14 + 8) + 'px' } as React.CSSProperties : {}) }}
                    onClick={pereklyuchit}
                    onDragOver={ev => ie.dragOver(ev, d.i)} onDragLeave={() => ie.dragLeave(d.i)}
                    onDrop={ev => { ev.preventDefault(); ie.drop(d.i); }}>
                    <td className={'num fx-n' + (ie.can && !pusto ? ' ie-ruchka' : '')} draggable={ie.can && !pusto}
                      title={ie.can && !pusto ? 'Потяните, чтобы перенести строку (с вложенными) в другое место или этап' : undefined}
                      onDragStart={ev => ie.dragStart(ev, d.i)} onDragEnd={ie.dragEnd}>
                      {!pusto && ie.can && <span className="ie-grip" aria-hidden="true">⋮⋮</span>}
                      {!pusto && d.razdel && <span className="pm">{svern ? '+' : '−'}</span>}</td>
                    {/* «Корп. / Эт. / Секц.» в режиме иерархии — один столбец «Контур» той же ширины
                        (владелец 2026-10-07: «контур вынеси влево от названия, только на суммарных») */}
                    <td className="fx-k ie-kontur" colSpan={estSek ? 3 : 2} style={{ width: estSek ? 150 : 80, maxWidth: estSek ? 150 : 80 }}
                      onClick={ev => ev.stopPropagation()}>
                      {d.razdel && r.id ? <IerKontur ie={ie} r={r} /> : null}</td>
                    <td className="fx-w ie-name" title={d.name + (d.razdel && r.kontur ? ' · контур: ' + r.kontur : '')} style={{ paddingLeft: 8 + r.glubina * 14 }}>
                      <IerImya ie={ie} i={d.i} prosto /></td>
                    <td className="col-itog col-b">{d.min ? fmt(d.min) : ''}</td>
                    <td className="col-itog col-ok">{d.max ? fmt(d.max) : ''}</td>
                    <td className="num col-itog col-e" title="окончание − начало по задачам">{d.min && d.max ? dnum(d.max)! - dnum(d.min)! : ''}</td>
                    <IerPolya ie={ie} i={d.i} onCard={n => ctx.onWorkCard?.(n)} />
                    {zaglushki}
                    {/* схема связей справа: блок работы в её строке, положение — уровень в цепочке */}
                    <td className="viz-c sv-cell" style={{ padding: 0 }}
                      title={!d.razdel && r.work_id ? 'Щелчок по строке — окно связей работы: предшественник и последователь' : undefined}
                      onClick={ev => { ev.stopPropagation();
                        // пустое место строки работы — то же окно, что двойной щелчок по блоку
                        // (владелец 2026-10-07: блок бывает далеко справа, а строка — вот она)
                        if (!d.razdel && r.work_id && !(ev.target as HTMLElement).closest('.sv-node')) setPop({ kind: 'svyazi', name: d.name, at: at(ev) }); }}>
                      <div style={{ width: svShirina, height: 1, position: 'relative' }}>
                        {!d.razdel && r.work_id && <span className="sv-lead" style={{ width: SV_OTSTUP + (svUr.get(d.name) || 0) * SV_SHAG }} />}
                        {!d.razdel && r.work_id && (
                          <div className={'sv-node' + (vybrSv === d.name ? ' on' : '')} data-sv={d.name}
                            style={{ left: SV_OTSTUP + (svUr.get(d.name) || 0) * SV_SHAG, width: SV_SHIRINA }}
                            title="Щелчок — подсветить связи работы; двойной — окно связей списком; тяните за кружок к работе, которая идёт следом"
                            onClick={() => setVybrSv(v => (v === d.name ? null : d.name))}
                            onDoubleClick={ev => setPop({ kind: 'svyazi', name: d.name, at: at(ev) })}>
                            <span className="sv-imya">{d.name}</span>
                            {can && <span className="sv-port" data-sv-port={d.name} title="Потяните к работе, которая идёт следом" />}
                          </div>)}
                      </div>
                    </td>
                  </tr>);
              }
              if (d.kind === 'vvod') {
                // задача над строкой ввода — для Ctrl+D «заполнить вниз», как в Excel
                const vyshe = (display.slice(0, first + i).reverse().find(x => x.kind === 'task') as { row: Row } | undefined)?.row ?? null;
                return <AddTaskRow key="vvod" ctx={ctx} meta={meta} struct={struct} glubina={d.glubina} vvod={vvod} fokus={fokus} vyshe={vyshe}
                  korpusa={(data?.korpusa || []).filter(k => k !== 'Объект')}
                  onSozdana={id => setVvodS(v => ({ ...v, posle: { tip: 'task', id } }))}
                  onTab={vvodTab} onShiftTab={vvodShiftTab}
                  onAdded={(m) => { saved(m); void load(); }} onFail={fail} />;
              }
              if (d.kind === 'rgr') {
                const spisokG = zadachiGruppy(d.id);
                const jeTek = tek?.tip === 'gruppa' && tek.id === d.id;
                const svernut = () => setRSvern(s0 => { const n = new Set(s0); if (n.has(d.id)) n.delete(d.id); else n.add(d.id); return n; });
                return (
                  <tr key={'g' + d.id} data-di={first + i} className={'grp etp rgr' + (d.glubina ? ' etp-' + Math.min(d.glubina, 3) : '') + (jeTek ? ' tek' : '')}
                    style={{ height: ROW_H }}
                    title="Группа ручного планирования. Щелчок — выбрать (Tab / Shift+Tab — в группу и из неё, Alt+↑/↓ — переставить, Delete — удалить с содержимым, Enter — строка ввода ниже); двойной щелчок или F2 — переименовать; Ctrl+щелчок — выделить её задачи"
                    onClick={e => {
                      if (e.ctrlKey || e.metaKey || e.shiftKey) {
                        const ids = spisokG.map(x => x.id), vse = ids.length > 0 && ids.every(x => sel.has(x));
                        const next = new Set(sel); ids.forEach(x => { if (vse) next.delete(x); else next.add(x); }); setSel(next);
                        return;
                      }
                      setTek({ tip: 'gruppa', id: d.id });
                    }}
                    onDoubleClick={() => { if (can) void pereimenovat(d.id, d.name); }}>
                    <td className="num fx-n" onClick={e => { e.stopPropagation(); svernut(); }}><span className="pm">{d.open ? '−' : '+'}</span></td>
                    <td className="fx-k"></td><td className="fx-e"></td><td className="col-zahv"></td>
                    <td className="fx-w" style={{ paddingLeft: 8 + d.glubina * 14 }}>{d.name}<span className="rb-grp-n">{d.n}</span>
                      {(() => {
                        const n = rSvyazi.filter(x => (x.ot.tip === 'gruppa' && x.ot.id === d.id) || (x.k.tip === 'gruppa' && x.k.id === d.id)).length;
                        return n > 0 && <span className="rgr-sv" title="Связи группы — щелчок: список, удалить"
                          onClick={e => { e.stopPropagation(); setPop({ kind: 'rsvyazi', gid: d.id, at: at(e) }); }}>⇄ {n}</span>;
                      })()}
                      {can && <span className="rgr-kop" title="Размножить группу: «Захватка 1» → «Захватка 2…» (Ctrl+D)"
                        onClick={e => { e.stopPropagation(); void razmnozhit(d.id, d.name); }}>⧉</span>}</td>
                    <td className="col-itog col-b">{d.min ? fmt(d.min) : ''}</td>
                    <td className="col-itog col-ok">{d.max ? fmt(d.max) : ''}</td>
                    <td className="num col-itog col-e" title="длительность группы: окончание − начало">
                      {d.min && d.max ? dnum(d.max)! - dnum(d.min)! : ''}</td>
                    {stolbecOsh && <td className="col-osh"></td>}
                    {zaglushki}
                    {viz && vizYacheyki(d.min, d.max, spisokG, 'sum-etap')}
                  </tr>);
              }
              if (d.kind === 'etap') {
                const closed = closedEtap.has(d.key);
                return (
                  <tr key={'e' + d.key + i} data-di={first + i} className={'grp etp' + (d.glubina ? ' etp-' + Math.min(d.glubina, 3) : '')} style={{ height: ROW_H }}
                    onClick={() => setClosedEtap(s => { const n = new Set(s); if (n.has(d.key)) n.delete(d.key); else n.add(d.key); return n; })}>
                    {/* плюс/минус в рамке, как в MS Project (владелец 2026-09-15) */}
                    <td className="num fx-n"><span className="pm">{closed ? '+' : '−'}</span></td><td className="fx-k"></td>
                    <td className="fx-e"></td><td className="col-zahv"></td>
                    <td className="fx-w" title={d.name} style={d.glubina ? { paddingLeft: 8 + d.glubina * 14 } : undefined}>{d.name}</td>
                    <td className="col-itog col-b">{d.min ? fmt(d.min) : ''}</td>
                    <td className="col-itog col-ok">{d.max ? fmt(d.max) : ''}</td>
                    <td className="num col-itog col-e" title="длительность этапа: окончание − начало">
                      {d.min && d.max ? dnum(d.max)! - dnum(d.min)! : ''}</td>
                    {stolbecOsh && <td className="col-osh"></td>}
                    {zaglushki}
                    {viz && vizYacheyki(d.min, d.max, poEtapu.get(d.key) || [], 'sum-etap')}
                  </tr>);
              }
              if (d.kind === 'stroka') {
                const spisokS = zadachiStroki(d.name);
                return (
                  <tr key={'s' + d.key + i} data-di={first + i} className="grp str-gpr" style={{ height: ROW_H }}
                    onClick={e => {
                      if (e.ctrlKey || e.metaKey || e.shiftKey) {   // как у работы: выделить все задачи строки
                        e.preventDefault(); e.stopPropagation();
                        const ids = spisokS.map(x => x.id);
                        const all = ids.length && ids.every(id => sel.has(id));
                        const next = new Set(sel);
                        ids.forEach(id => { if (all) next.delete(id); else next.add(id); });
                        setSel(next);
                        return;
                      }
                      setOpenStroki(st => { const n = new Set(st); if (n.has(d.key)) n.delete(d.key); else n.add(d.key); return n; });
                    }}>
                    <td className="num fx-n"><span className="pm">{d.open ? '−' : '+'}</span></td><td className="fx-k"></td>
                    <td className="fx-e"></td><td className="col-zahv"></td>
                    <td className="fx-w" title={'Строка ГПР «' + d.name + '»: ' + d.rabot + ' работ с общими сроками на этаже. Клик — развернуть или свернуть; полоса на Ганте двигает и растягивает строку целиком'}
                      style={{ paddingLeft: 8 + glubinaRaboty(d.name) * 14 }}>
                      {d.name}<span className="str-gpr-n" title="Работ в строке ГПР">{d.rabot}</span></td>
                    <td className="col-itog col-b">{d.min ? fmt(d.min) : ''}</td>
                    <td className="col-itog col-ok">{d.max ? fmt(d.max) : ''}</td>
                    <td className="num col-itog col-e" title="длительность: окончание − начало">
                      {d.min && d.max ? dnum(d.max)! - dnum(d.min)! : ''}</td>
                    {stolbecOsh && <td className="col-osh"></td>}
                    {zaglushki}
                    {viz && vizYacheyki(d.min, d.max, spisokS, 'sum-rab')}
                  </tr>);
              }
              if (d.kind === 'work') {
                return (
                  <tr key={'w' + d.name + i} data-grp={d.name} data-di={first + i} className="grp"
                    style={{ height: ROW_H }} onClick={e => clickWorkGroup(e, d.name)}>
                    <td className="num fx-n"><span className="pm">{d.open ? '−' : '+'}</span></td><td className="fx-k"></td>
                    <td className="fx-e"></td><td className="col-zahv"></td>
                    <td className="fx-w" title="Клик по строке — развернуть или свернуть работу"
                      style={{ paddingLeft: 8 + (glubinaRaboty(d.name) + (d.vStroke ? 1 : 0)) * 14 }}>{d.name}</td>
                    <td className="col-itog col-b">{d.min ? fmt(d.min) : ''}</td>
                    <td className="col-itog col-ok">{d.max ? fmt(d.max) : ''}</td>
                    <td className="num col-itog col-e" title="длительность: окончание − начало">
                      {d.min && d.max ? dnum(d.max)! - dnum(d.min)! : ''}</td>
                    {stolbecOsh && <td className="col-osh"></td>}
                    {zaglushki}
                    {viz && vizYacheyki(d.min, d.max, poRabote.get(d.name), 'sum-rab')}
                  </tr>);
              }
              const row = d.row;
              const rk = row.korpus || planKorpus;
              const fi = floorOf(row);
              const cs = fi?.tip && fi.tip !== 'Типовой' ? tipColor(fi.tip) : null;
              const blDelta = row.bl && row.okonchanie ? dnum(row.okonchanie)! - dnum(row.bl)! : 0;
              return (
                <tr key={row.id} data-tid={row.id} data-di={first + i}
                  className={(row.ruchnoe ? 'ruch' : '') + (row.iskl ? ' iskl' : '')
                    + (sel.has(row.id) ? ' psel' : '')
                    + (ruch && tek?.tip === 'task' && tek.id === row.id ? ' tek' : '')
                    // чья карточка открыта в нижней панели (2026-09-14)
                    + (detVnizu && pop?.kind === 'task' && pop.row.id === row.id ? ' det-tek' : '')
                    + (problemy.get(row.id)
                      ? ' prob' + (problemy.get(row.id)!.gruppa === 'ошибка' ? ' prob-osh' : '')
                      : '')}
                  title={problemy.get(row.id)
                    ? ({ 'ошибка': 'Ошибка: ', 'конфликт': 'Конфликт: ' }[problemy.get(row.id)!.gruppa] || 'Отставание: ')
                      + problemy.get(row.id)!.chto.join('; ')
                      + '. Разбор — на экране «Проверки»'
                    : undefined}
                  style={{ height: ROW_H }} onClickCapture={e => clickRow(e, row)}>
                  {/* № — системный ид задачи, не редактируется (владелец 2026-09-08);
                      им ссылаются на задачу в ручных предшественниках */}
                  {/* в ручном щелчок только выбирает строку, карточка — двойным (владелец 2026-10-27) */}
                  <td className={'num fx-n' + (ruch ? '' : ' pw-link')}
                    title={'№ ' + (row.nomer ?? '—') + ' — системный номер задачи (по нему её указывают '
                      + 'в ручных предшественниках). ' + (ruch ? 'Двойной щелчок' : 'Клик') + ' — карточка задачи'}
                    onClick={ruch ? undefined : e => { e.stopPropagation(); setPop({ kind: 'task', row, vkladka: 'obzor' }); }}
                    onDoubleClick={ruch ? () => setPop({ kind: 'task', row, vkladka: 'obzor' }) : undefined}>
                    {row.nomer ?? ''}</td>
                  {ruch && can
                    ? <EditTd row={row} col={KORPUS_RUCH} can={can} podr={['Объект', ...(data?.korpusa || []).filter(k => k !== 'Объект')]}
                        onBadDate={badDate} cls="fx-k" title="Корпус: щелчок — выбрать; «Объект» — задача на весь объект"
                        onSave={(v, c) => patchTask(row, c.key, v, c)} />
                    : <td className="fx-k" title={rk || undefined}>{rk || ''}</td>}
                  {/* окно этажа из «Планирования» убрано (владелец 2026-10-17) — этаж правится на «Этажах» */}
                  {ruch && can
                    ? <EditTd row={row} col={ETAZH_RUCH} can={can} podr={podr} onBadDate={badDate} cls="num fx-e"
                        title="Этаж или ярус — любой текст: щелчок и печатайте, Enter — сохранить, Delete — очистить"
                        onSave={(v, c) => patchTask(row, c.key, v, c)} />
                    : <td className="num fx-e" style={cs ? { background: cs.bg, borderLeft: '3px solid ' + cs.bd } : undefined}>
                        {row.etazh ?? ''}</td>}
                  {/* секция внутри этажа (#70); у задач без секции — захватка, как было; в ручном — «Место», правится (#126) */}
                  {ruch && can
                    ? <EditTd row={row} col={MESTO_RUCH} can={can} podr={podr} onBadDate={badDate} cls="col-zahv"
                        title="Место (захватка, участок) — любой текст: щелчок и печатайте, Enter — сохранить, Delete — очистить"
                        onSave={(v, c) => patchTask(row, c.key, v, c)} />
                    : <td className="col-zahv" title={row.sek ? 'Секция ' + row.sek : undefined}>{row.sek || row.zahvatka || ''}</td>}
                  {/* карточка одна: клик по работе открывает её на вкладке «Работа» (2026-09-08) */}
                  <td className={'fx-w' + (ruch ? '' : ' pw-link')}
                    title={ruch ? 'Щелчок — выбрать строку; двойной щелчок — карточка задачи' : 'Открыть карточку: вкладка «Работа»'}
                    style={grouped ? { paddingLeft: 44 } : ruch ? { paddingLeft: 8 + (d.glubina || 0) * 14 } : undefined}
                    onClick={ruch ? undefined : e => { e.stopPropagation(); setPop({ kind: 'task', row, vkladka: 'rabota' }); }}
                    onDoubleClick={ruch ? () => setPop({ kind: 'task', row, vkladka: 'rabota' }) : undefined}>
                    {!bezKE ? row.rabota || '' : <>
                      {/* название работы — если над задачей нет строки работы; корпус — если не уровень группировки */}
                      {!(grouped && sRabotoj) && <span className="t-rab">{row.rabota || ''}</span>}
                      <span className="t-gde">
                        {!(grouped && urovni.includes('korpus')) && rk && <span>{rk}</span>}
                        {/* без цветной плашки типа (владелец 2026-10-18: «выделение на этажах не нужно, функции нет») */}
                        <span className="t-et">
                          {row.etazh != null ? row.etazh + ' эт.' : 'на корпус'}</span>
                        {(row.sek || row.zahvatka) && <span>{row.sek ? 'секц. ' + row.sek : 'захв. ' + row.zahvatka}</span>}
                      </span>
                    </>}
                    {/* захватки размечены на схеме (#109): факт по этой задаче отмечается
                        на чертеже, а в шахматке показывается уже посчитанный итог */}
                    {row.shema && (
                      <span className="zn-shema"
                        title="Захватки размечены на схеме: факт отмечается на чертеже. Клик — вкладка «Схема»"
                        onClick={e => { e.stopPropagation(); setPop({ kind: 'task', row, vkladka: 'shema' }); }}>▦</span>
                    )}</td>
                  <EditTd row={row} col={NACH_ITOG} can={can} podr={podr} onBadDate={badDate}
                    cls={'col-itog col-b' + (row.nach_vr ? ' ruch-data' : '')}
                    title={row.nach_vr ? 'Начало задано вручную — от него считаются сроки. Щелчок — изменить, пусто — вернуть расчёт'
                      : 'Расчётное начало. Щелчок — задать вручную (сроки пересчитаются от неё); как получена дата — вкладка «Расчёт начала» в карточке'}
                    onSave={(v, c) => patchSrok(row, c.key, v, c)} />
                  <EditTd row={row} col={OKONCH_ITOG} can={can} podr={podr} onBadDate={badDate}
                    cls={'col-itog col-ok' + (row.okonch_vr ? ' ruch-data' : '')}
                    title={row.okonch_vr ? 'Окончание задано вручную — от него считаются сроки. Щелчок — изменить, пусто — вернуть расчёт'
                      : 'Расчётное окончание. Щелчок — задать вручную (сроки пересчитаются от неё); как получена дата — вкладка «Расчёт окончания» в карточке'}
                    onSave={(v, c) => patchSrok(row, c.key, v, c)} />
                  <EditTd row={row} col={DLIT_ITOG} can={can} podr={podr} onBadDate={badDate}
                    cls={'num col-itog col-e' + (row.dlit_r != null ? ' ruch-data' : '')}
                    title={row.dlit_r != null ? 'Длительность задана вручную. Щелчок и печатайте — изменить, Delete — вернуть автоматическую (' + (row.dlit_avto ?? '—') + ' дн.)'
                      : 'Длительность по справочнику. Щелчок и печатайте число дней — задать вручную; сроки пересчитаются сразу'}
                    onSave={(v, c) => patchSrok(row, c.key, v, c)} />
                  {ruch && <td className={'col-ruch-pred lnkc' + (row.mp?.length ? ' manual' : '')}
                    title={(predRuchText(row) || 'Предшественник не задан') + '. Клик — открыть карточку связей задачи'}
                    onClick={() => setPop({ kind: 'task', row, vkladka: 'svyazi' })}>
                    {predRuchText(row)}</td>}
                  {stolbecOsh && (() => {
                    const pr = problemy.get(row.id);
                    const tekst = pr ? pr.chto.join('; ') : '';
                    return <td className={'col-osh' + (pr?.gruppa === 'ошибка' ? ' osh' : '')} title={tekst || undefined}>
                      {pr ? (pr.gruppa === 'ошибка' ? '● ' : '○ ') + tekst : ''}</td>;
                  })()}
                  {cols.hand && HAND.map(col => (
                    <EditTd key={col.key} row={row} col={col} can={can} podr={podr} onBadDate={badDate}
                      cls={'num col-hand' + (col.cls ? ' ' + col.cls : '') + (col.manual(row) ? ' manual' : '')}
                      onSave={(v, c) => patchTask(row, col.key, v, c)} />
                  ))}
                  {cols.res && RES.map(col => (
                    <EditTd key={col.key} row={row} col={col} can={can} podr={podr} onBadDate={badDate}
                      cls={'num col-res' + (col.manual(row) ? ' manual' : '')}
                      onSave={(v, c) => patchTask(row, col.key, v, c)} />
                  ))}
                  {cols.pred && <>
                    <td className="num col-pred" title="очередь в рамках корпуса и работы">{rank.get(row.id) ?? ''}</td>
                    <td className={'col-pred expl lnkc' + (row.ml || row.lo ? ' lnk-on' : '')}
                      title={'срок предшественника по связи. Клик — предшественники задачи'
                        + (row.ml ? '; ручных связей: ' + row.ml : '') + (row.lo ? '; отключено штатных: ' + row.lo : '')}
                      onClick={() => setPop({ kind: 'task', row, vkladka: 'svyazi' })}>{fmt(row.ps)}</td>
                    <td className="col-pred" title="окончание предыдущего этажа бригады">{fmt(row.prs)}</td>
                    <td className={'col-pred' + (row.nach_vr ? ' manual' : '')} title="ручное начало задачи">{fmt(row.nach_vr)}</td>
                    <td className={'col-pred' + (row.okonch_vr ? ' manual' : '')} title="ручное окончание задачи">{fmt(row.okonch_vr)}</td>
                    {!ruch && <td className={'col-pred lnkc' + (row.mp && row.mp.length ? ' manual' : '')}
                      title="ручные предшественники (номера задач). Клик — назначить в карточке задачи"
                      onClick={() => setPop({ kind: 'task', row, vkladka: 'svyazi' })}>
                      {predRuchText(row)}</td>}
                  </>}
                  {cols.fakt && (() => {
                    // клик по факту — очистить его (планер, 2026-09-11); с Ctrl/Shift — выделение
                    const chist = can && estFakt(row);
                    const fk = (e: React.MouseEvent) => {
                      if (chist && !e.ctrlKey && !e.metaKey && !e.shiftKey) void ochistitFakt([row.id]);
                    };
                    const cl = chist ? ' fakt-chist' : '';
                    const tt = chist ? 'Клик — очистить факт задачи' : undefined;
                    return <>
                      <td className={'num col-fakt' + cl} title={tt} onClick={fk}>{row.vn ? fmt(row.vn) : ''}</td>
                      <td className={'num col-fakt' + cl} title={tt} onClick={fk}>{row.vo ? fmt(row.vo) : ''}</td>
                      <td className={'col-fakt' + cl} title={tt} onClick={fk}>{row.sv || ''}</td>
                      <td className={'num col-fakt' + cl} title={tt} onClick={fk}>{row.proc != null ? row.proc + '%' : ''}</td>
                    </>;
                  })()}
                  {cols.sprav && <>
                    <td className="num col-sprav">{row.dlit_avto ?? ''}</td>
                    <td className="num col-sprav">{fi?.prioritet ?? ''}</td>
                  </>}
                  {cols.base && <>
                    <td className="col-base">{fmt(row.bln)}</td>
                    <td className={'col-base' + (blDelta > 0 ? ' late' : blDelta < 0 ? ' early' : '')}
                      title={blDelta > 0 ? 'позже базового на ' + blDelta + ' дн' : blDelta < 0 ? 'раньше базового на ' + -blDelta + ' дн' : ''}>
                      {fmt(row.bl)}</td>
                  </>}
                  {kritColumn && <td className={'num col-krit' + (critical(row) ? ' krit-zero' : '')}
                    title={kritMap.get(row.id)?.zakreplena
                      ? 'Дата закреплена. Запас показывает допустимый сдвиг при снятии фиксации; дата не меняется.'
                      : critical(row) ? 'Критический путь: задержка переносит окончание проекта'
                      : 'Запас календарных дней; у оконченных задач не применяется'}>
                    {kritMap.get(row.id)?.zapas_dnej == null ? '—'
                      : kritMap.get(row.id)!.zapas_dnej!.toLocaleString('ru-RU', { maximumFractionDigits: 3 })}
                    {kritMap.get(row.id)?.zakreplena ? ' ▣' : ''}
                  </td>}
                  {viz && vizYacheyki(row.nachalo, row.okonchanie, undefined,
                    row.sv === 'СМР окончены' || row.sv === 'СМР приняты' ? 'done'
                      : row.sv === 'СМР начаты' ? 'act' : '', row)}
                </tr>);
            })}
            {padBottom > 0 && <tr style={{ height: padBottom }}><td colSpan={33} style={{ padding: 0, border: 0 }} /></tr>}
            {/* строка быстрого ввода — только в «Ручном планировании» (владелец 2026-09-08); с #126 она
                стоит в дереве на своём месте (display, kind 'vvod'), а не внизу таблицы */}
            {/* строка «＋ новая работа» в «Планировании» убрана (владелец 2026-10-15: «пока оставим
                только в работах» — ввод работ на экране «Структура работ»); компонент AddWorkRow цел */}
          </tbody>
        </table>
      </div>
      </>
      )}

      {/* нижняя панель деталей (просьба владельца 2026-09-14): та же карточка
          задачи, но под таблицей — строку видно, соседнюю задачу открывают
          кликом, панель переключается на неё. Появляется сама при клике по задаче,
          закрывается крестиком или Esc (владелец 2026-09-16) */}
      {panel && (
        <div className="det-panel det-sprava" style={{ width: detH, top: Math.max(8, sverhu - window.scrollY) }}>
          <div className="det-grip" title="Тяните влево или вправо — панель станет шире или уже"
            onMouseDown={tyanutPanel} />
          <div className="det-body">
            {/* key: при переходе на другую задачу карточка пересобирается,
                иначе на мгновение осталась бы прежняя */}
            <TaskCard key={pop.row.id} pid={ctx.pid} row={pop.row} korpusa={(data?.korpusa || []).filter(k => k !== 'Объект')}
              works={Object.keys(meta).sort()} struct={struct} podr={podr} can={can}
              ruchnaya={ruchnayaRabota(pop.row.rabota)} vnizu problemy={problemy.get(pop.row.id) || null}
              vkladka={pop.vkladka} onClose={() => setPop(null)}
              onChanged={m => { saved(m); void load(); }} onFail={fail} />
          </div>
        </div>
      )}

      {/* в ручном — без текста под таблицей (владелец 2026-10-27): подсказки — в справке и в подсказках ячеек */}
      {!ruch && <div className="pw-muted plan-niz" style={{ marginTop: 8 }}>
        {data ? (data.korpus === '*' ? 'Все корпуса' : 'Корпус ' + data.korpus) + ': показано ' + rows.length
          + ' из ' + data.total + ' задач. ' : ''}
        {oshibki ? 'Показаны только задачи с замечаниями: розовая строка — ошибка, песочная — конфликт; '
          + 'что именно не так — в подсказке строки (наведите мышь). Все задачи — повторным нажатием «Ошибки». '
          : ''}
        {ruch ? 'Ручное планирование: строка ＋ — работа, корпус (пусто — на объект), этаж и место любым текстом, дни, '
          + 'предшественник; Enter — сохранить, курсор остаётся для следующей. Tab в строке ＋: набрано название — '
          + 'новая группа, пусто — вложить в строку выше; Shift+Tab — на уровень выше. У выбранной строки: Tab / Shift+Tab, '
          + 'Alt+↑/↓ — переставить, Delete — удалить (вернуть — ↺), Enter — строка ＋ под ней, F2 — переименовать группу. '
          + 'Связь — протяните полосу на Ганте вверх или вниз на другую (задачу или группу); выбранные с Ctrl — Ctrl+F2 «связать цепочкой». '
          + '⧉ у группы или Ctrl+D — размножить её («Захватка 1» → «Захватка 2…»). '
          : ''}
        Полосы Ганта: серая — обычная, жёлтая — закреплена фактом или ручной датой, зелёная — СМР окончены, красная — есть замечания. Жёлтая строка — ручное планирование/факт; синее — введено вручную; клик — правка.
        Ctrl+клик — выделить строки для пакетной правки (Shift — диапазон, Ctrl+клик по группе — вся работа).
      </div>}

      {pop?.kind === 'rsvyazi' && (
        <Pop at={pop.at} title={'Связи: ' + imyaUzla({ tip: 'gruppa', id: pop.gid })} onClose={() => setPop(null)} width={420}>
          {rSvyazi.filter(x => (x.ot.tip === 'gruppa' && x.ot.id === pop.gid) || (x.k.tip === 'gruppa' && x.k.id === pop.gid))
            .map(x => (
              <div key={x.id} className="rgr-sv-str">
                <span>{imyaUzla(x.ot)} → {imyaUzla(x.k)}{x.zapas ? ' (' + (x.zapas > 0 ? '+' : '') + x.zapas + ' дн.)' : ''}</span>
                {can && <button className="sec" title="Удалить связь" onClick={() => void udalitSvyaz(x.id)}>✕</button>}
              </div>))}
          <div className="pw-muted" style={{ marginTop: 6 }}>Группа-предшественник: следующая строка ждёт все её задачи.
            Группа-последователь: все её задачи ждут предшественника. Новая связь — протяните полосу на Ганте вверх или вниз на другую.</div>
        </Pop>)}
      {pop?.kind === 'svyazi' && <SvyaziRaboty pid={ctx.pid} name={pop.name} at={pop.at} can={can}
        raboty={Object.keys(meta).sort((a, b) => a.localeCompare(b, 'ru'))}
        onClose={() => setPop(null)} onFail={fail}
        onChanged={() => { saved('Связи сохранены. Даты — после «Пересчитать».'); void load(); }} />}
      {pop?.kind === 'explain' && <ExplainPop at={pop.at} row={pop.row} onClose={() => setPop(null)} onFail={fail}
        onLinks={e => setPop({ kind: 'links', row: pop.row, at: at(e) })} />}
      {pop?.kind === 'dlit' && <DlitPop at={pop.at} row={pop.row} work={meta[pop.row.rabota]} korpus={planKorpus}
        onClose={() => setPop(null)} onFail={fail}
        onSaved={() => { setPop(null); saved(); void load(); }} />}
      {pop?.kind === 'links' && <LinksPop at={pop.at} row={pop.row} korpusa={(data?.korpusa || []).filter(k => k !== 'Объект')} works={Object.keys(meta).sort()}
        struct={struct} planKorpus={planKorpus} can={can} onClose={() => setPop(null)} onFail={fail}
        onSaved={() => { saved(); void load(); }} />}
      {pop?.kind === 'bulklinks' && <BulkLinksPop at={pop.at} pid={ctx.pid} ids={[...sel]} korpusa={(data?.korpusa || []).filter(k => k !== 'Объект')}
        works={Object.keys(meta).sort()} struct={struct} onClose={() => setPop(null)} onFail={fail}
        onDone={m => { setPop(null); saved(m); setSel(new Set()); void load(); }} />}
    </div>
  );
}

/** Индекс «Длит.» блока «Расчёт» в HEAD: после него встаёт столбец «Ошибка» */
const HEAD_ITOG_POSLEDNIJ = 7;
/** Заголовки второй строки шапки — порядок должен совпадать с SORT_GET */
const HEAD: { t: string; c: string; ttl?: string }[] = [
  { t: '№', c: 'col-nom', ttl: '№ задачи (ИД): по нему задаются ручные предшественники. Клик — изменить номер' },
  { t: 'Корп.', c: 'col-korp', ttl: 'Корпус' }, { t: 'Эт.', c: 'col-et', ttl: 'Этаж' }, { t: 'Секц.', c: 'col-zahv', ttl: 'Секция внутри этажа (у работ с раскаткой «по этажам и секциям»); у остальных задач здесь захватка' }, { t: 'Работа', c: 'col-work' },
  { t: 'Начало', c: 'col-itog col-b w-d', ttl: 'Итоговое расчётное начало. Клик по дате в строке — как она получена' },
  { t: 'Окончание', c: 'col-itog col-ok w-d', ttl: 'Итоговое расчётное окончание. Клик по дате в строке — как она получена' },
  { t: 'Длит.', c: 'col-itog col-e w-n', ttl: 'Итоговая длительность: ручная, а если пусто — из справочника' },
  { t: 'Длит.', c: 'col-hand w-n', ttl: 'Ручная длительность задачи — решение планера (пусто — действует длительность из справочника работы)' },
  { t: 'Декор. длит.', c: 'col-hand w-n', ttl: 'Декоративная длительность: ставится растягиванием суммарной полосы на Ганте. Расчёт учитывает её, «Расчётный» показывает график без неё; снимается сама при факте или ручной правке' },
  { t: 'Сдвиг начала', c: 'col-hand w-n', ttl: 'Запас: на сколько дней задача начинается позже, чем позволяет расчёт' },
  { t: 'Декор. начало', c: 'col-hand w-d', ttl: 'Декоративное начало «не раньше»: сдвиг суммарной полосы ставит его всей группе одной датой. Расчёт учитывает, «Расчётный» показывает график без него; снимается двойным кликом по суммарной полосе, при факте или ручной правке' },
  { t: 'Начало', c: 'col-hand w-d', ttl: 'Ручное начало задачи: фиксирует строку' },
  { t: 'Оконч.', c: 'col-hand w-d', ttl: 'Ручное окончание задачи: фиксирует строку' },
  { t: 'Объём', c: 'col-res w-n', ttl: 'Объём задачи: длительность = объём ÷ (выработка × численность работы)' },
  { t: 'Приоритет', c: 'col-res w-n', ttl: 'Ручной приоритет в очереди бригады: меньше — раньше' },
  { t: 'Бригада', c: 'col-res w-n', ttl: 'Номер бригады: разные бригады одной работы идут параллельно' },
  { t: 'Подрядчик', c: 'col-res', ttl: 'Подрядчик задачи — из списка «Подрядчики» в «Списках»' },
  { t: 'Оч.', c: 'col-pred w-n', ttl: 'Расчётная позиция задачи в очереди в рамках корпуса и работы' },
  { t: 'По связи', c: 'col-pred w-d', ttl: 'Срок предшественника по связи (окончание, для НН — начало)' },
  { t: 'По ресурсу', c: 'col-pred w-d', ttl: 'Окончание предыдущего этажа той же бригады' },
  { t: 'Нач. вруч.', c: 'col-pred w-d' }, { t: 'Оконч. вруч.', c: 'col-pred w-d' }, { t: 'Ручн. предш.', c: 'col-pred' },
  { t: 'Нач. факт', c: 'col-fakt w-d' }, { t: 'Оконч. факт', c: 'col-fakt w-d' }, { t: 'Статус факт', c: 'col-fakt' },
  { t: '% факт', c: 'col-fakt w-n' }, { t: 'Авто длит.', c: 'col-sprav w-n' }, { t: 'Авто приор.', c: 'col-sprav w-n' },
  { t: 'Баз. начало', c: 'col-base w-d' },
  { t: 'Баз. оконч.', c: 'col-base w-d', ttl: 'красным — расчёт позже базы, зелёным — раньше' },
];

/** Ячейка с правкой по клику: текст → поле ввода или список */
function EditTd({ row, col, can, cls, title, podr, onSave, onBadDate }: {
  row: Row; col: Col; can: boolean; cls?: string; title?: string; podr: string[];
  onSave: (v: string | null, col: Col) => Promise<void>; onBadDate: () => void;
}) {
  // как в Excel (владелец 2026-10-18): первый щелчок выделяет ячейку; печать заменяет значение, Enter/F2/двойной
  // щелчок — правка существующего, Delete — очистить, Esc или щелчок мимо — снять выделение
  const [edit, setEdit] = useState(false);
  const [vyb, setVyb] = useState(false);
  const [nachalo, setNachalo] = useState<string | null>(null);   // с чего начать ввод: набранный символ или значение
  const value = col.get(row);
  const nachatPravku = (s: string) => { setNachalo(s); setEdit(true); setVyb(false); };
  if (!edit) {
    return <td className={(cls || '') + (can ? ' edit' : '') + (vyb ? ' yach-vyb' : '')} title={title}
      tabIndex={can ? 0 : undefined}
      ref={el => { if (el && vyb && document.activeElement !== el) el.focus(); }}
      // ячейка со списком (корпус, подрядчик) открывает список с первого щелчка (владелец 2026-10-27)
      onClick={e => { if (can && !e.ctrlKey && !e.metaKey && !e.shiftKey) { if (col.options) nachatPravku(value); else setVyb(true); } }}
      onDoubleClick={() => { if (can) nachatPravku(value); }}
      onBlur={() => setVyb(false)}
      onKeyDown={e => {
        if (!vyb) return;
        if (e.key === 'Escape') { setVyb(false); return; }
        if (e.key === 'Enter' || e.key === 'F2') { e.preventDefault(); nachatPravku(value); return; }
        if (e.key === 'Delete' || e.key === 'Backspace') { e.preventDefault(); if (value && col.manual(row)) void finish(true, ''); return; }
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) { e.preventDefault(); nachatPravku(e.key); }
      }}>{value}</td>;
  }
  async function finish(save: boolean, raw: string) {
    setEdit(false);
    if (!save) return;
    const t = raw.trim();
    if (t === value) return;
    let val: string | null = t === '' ? null : t;
    if (col.date) {
      const iso = t === '' ? null : parseDate(t);
      // неразобранная дата: сообщение пользователю, на сервер ничего не уходит
      if (iso === undefined) { onBadDate(); return; }
      val = iso;
    }
    await onSave(val, col);
  }
  const opts = col.options ? col.options(podr) : null;
  return (
    <td className={(cls || '') + ' editing'} title={title}>
      {opts
        ? <select autoFocus defaultValue={value} onChange={e => void finish(true, e.target.value)} onBlur={() => setEdit(false)}
            onKeyDown={e => { if (e.key === 'Escape') setEdit(false); }}
            // список раскрывается сразу, без второго щелчка (браузер без showPicker — просто фокус)
            ref={el => { if (el && !el.dataset.otkryt) { el.dataset.otkryt = '1'; try { el.showPicker(); } catch { /* старый браузер */ } } }}>
            {(opts.includes(value) ? opts : [value, ...opts]).map((o, i) => <option key={o + i} value={o}>{o}</option>)}
          </select>
        : <input autoFocus defaultValue={nachalo ?? value} style={{ width: '100%' }}
            onFocus={e => { const n = e.currentTarget.value.length; e.currentTarget.setSelectionRange(n, n); }}
            onBlur={e => void finish(true, e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') void finish(true, (e.target as HTMLInputElement).value);
              if (e.key === 'Escape') setEdit(false);
            }} />}
    </td>
  );
}

/** есть ли у задачи ручной факт (статус, даты или процент) */
const estFakt = (r: { sv?: string | null; vn?: string | null; vo?: string | null; proc?: number | null }) =>
  !!(r.sv || r.vn || r.vo || r.proc != null);

function SelBar({ count, ruchnyh, sFaktom, podr, can, onApply, onClear, onLinks, onDelete, onClearFakt, onCepochka }: {
  count: number;
  /** ручное планирование (#127): связать выбранные задачи цепочкой в порядке таблицы */
  onCepochka?: () => void;
  /** сколько из выбранных с фактом — у них его можно очистить (2026-09-11) */
  sFaktom: number; onClearFakt: () => void;
  /** сколько из выбранных заведено вручную — только их можно удалить (2026-09-10) */
  ruchnyh: number;
  podr: string[]; can: boolean;
  onApply: (body: Record<string, unknown>) => Promise<boolean>; onClear: () => void;
  onLinks: (e: React.MouseEvent) => void; onDelete: () => Promise<void>;
}) {
  const empty = { dlit: '', obyom: '', prior: '', brig: '', zapas: '', nach: '', okonch: '', podr: '', iskl: '' };
  const [v, setV] = useState(empty);
  const f = (k: keyof typeof empty) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setV(s => ({ ...s, [k]: e.target.value }));
  async function apply() {
    const body: Record<string, unknown> = {};
    let any = false;
    const numF = (raw: string, key: string) => { const t = raw.trim(); if (t === '') return; any = true; body[key] = t === '-' ? null : t; };
    numF(v.dlit, 'dlit_ruchnaya'); numF(v.obyom, 'obyom'); numF(v.prior, 'prioritet_vruchnuyu');
    numF(v.brig, 'nomer_brigady'); numF(v.zapas, 'zapas');
    for (const [raw, key] of [[v.nach, 'nachalo_vruchnuyu'], [v.okonch, 'okonchanie_vruchnuyu']] as [string, string][]) {
      const t = raw.trim();
      if (t === '') continue;
      any = true;
      if (t === '-') { body[key] = null; continue; }
      const iso = parseDate(t);
      if (iso === undefined) return;
      body[key] = iso;
    }
    if (v.podr) { any = true; body.podryadchik = v.podr === '-' ? null : v.podr; }
    if (v.iskl) { any = true; body.isklyuchena = v.iskl === 'да'; }
    if (!any) return;
    if (await onApply(body)) setV(empty);
  }

  /** удаление выбранного — без вопроса (#125, 2026-10-27): удаление стало шагом отмены,
   *  «↺» возвращает задачи вместе со связями; раскатанные сервер пропускает и называет число */
  async function udalit() { await onDelete(); }
  return (
    <div className="bar" style={{ background: '#eef3fd', border: '1px solid #bcd0f7', borderRadius: 8, padding: '6px 10px', margin: '6px 0' }}>
      <b>Выбрано: {count}</b>
      <label>Длит. <input style={{ width: 60 }} value={v.dlit} onChange={f('dlit')} /></label>
      <label>Объём <input style={{ width: 70 }} value={v.obyom} onChange={f('obyom')} /></label>
      <label>Приор. <input style={{ width: 60 }} value={v.prior} onChange={f('prior')} /></label>
      <label>Бриг. <input style={{ width: 60 }} value={v.brig} onChange={f('brig')} /></label>
      <label>Подрядчик <select value={v.podr} onChange={f('podr')}>
        <option value="">— не менять —</option><option value="-">— очистить —</option>
        {podr.map(x => <option key={x} value={x}>{x}</option>)}</select></label>
      <label>Зап. <input style={{ width: 60 }} value={v.zapas} onChange={f('zapas')} /></label>
      <label>Нач. вруч. <input style={{ width: 100 }} placeholder="ДД.ММ.ГГГГ" value={v.nach} onChange={f('nach')} /></label>
      <label>Оконч. вруч. <input style={{ width: 100 }} placeholder="ДД.ММ.ГГГГ" value={v.okonch} onChange={f('okonch')} /></label>
      <label title="Исключить выбранные задачи из модели или вернуть">Искл. <select value={v.iskl} onChange={f('iskl')}>
        <option value="">— не менять —</option><option value="да">исключить</option><option value="-">вернуть</option></select></label>
      {can && <button onClick={() => void apply()}>Применить к выбранным</button>}
      {can && <button className="sec" title="Назначить выбранным задачам ручного предшественника" onClick={onLinks}>Предшественник…</button>}
      {can && onCepochka && <button className="sec" title="Каждая следующая выбранная задача (по порядку в таблице) — после окончания предыдущей"
        onClick={onCepochka}>Связать цепочкой ({count})</button>}
      {can && ruchnyh > 0 && (
        <button className="dlg-opasno"
          title="Удалить выбранные задачи ручных работ вместе с их связями; вернуть — стрелкой ↺. Задачи, созданные раскаткой, останутся"
          onClick={() => void udalit()}>Удалить задачи ({ruchnyh})</button>
      )}
      {can && sFaktom > 0 && (
        <button className="dlg-opasno" title="Стереть статус, даты и процент факта у выбранных задач"
          onClick={onClearFakt}>Очистить факт ({sFaktom})</button>
      )}
      <button className="sec" onClick={onClear}>Снять выделение</button>
      <span className="pw-muted">пусто — не менять, «-» — очистить</span>
    </div>
  );
}

/** Окно «почему такая дата» по клику на начало/окончание (переделка 2026-10-05, владелец:
    «хотел видеть, чем обусловлена дата, а вижу кучу текста, не понимаю, куда смотреть»).
    Сверху — ответ одной строкой: что держит начало и как получено окончание. Ниже —
    остальные причины, короче и бледнее, с тем, на сколько дней они раньше. Мягкие связи
    и связи без предшественника — под «Не держат дату». Полный разбор — в карточке задачи. */
function ExplainPop({ at: a, row, onClose, onFail, onLinks }:
  { at: Anchor; row: Row; onClose: () => void; onFail: (m: string) => void; onLinks: (e: React.MouseEvent) => void }) {
  const [d, setD] = useState<Explain | null>(null);
  useEffect(() => {
    void (async () => {
      const r = await call<Explain>('get', p('/api/tasks/{tid}/explain', { tid: row.id }));
      if (!r.ok) { onFail(r.error.message); onClose(); return; }
      setD(r.data);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id]);
  if (!d) return null;
  const dni = (x: string | null | undefined, y: string | null | undefined) =>
    x && y ? Math.round((Date.parse(x) - Date.parse(y)) / D1) : null;
  const derzhat = d.kandidaty.filter(k => k.deystvuet);
  const prochie = d.kandidaty.filter(k => !k.deystvuet)
    .sort((x, y) => (y.data || '').localeCompare(x.data || ''));
  const zakrepleno = d.tip !== 'расчёт';
  return (
    <Pop at={a} title={d.rabota + (d.etazh ? ' · этаж ' + d.etazh : '')} onClose={onClose} width={480}>
      <div className="ex-otvet">
        <div className="ex-str">
          <span className="ex-lab">Начало</span><b className="ex-data">{d.nachalo ? fmt(d.nachalo) : '—'}</b>
          <span className="ex-pochemu">
            {zakrepleno || !derzhat.length
              ? <b>{d.nachalo_itog}</b>
              : derzhat.map((k, i) => <span key={i}><b>{k.nazvanie}</b>
                  {k.poyasnenie && <span className="ex-pod">{k.poyasnenie}</span>}</span>)}
          </span>
        </div>
        <div className="ex-str">
          <span className="ex-lab">Окончание</span><b className="ex-data">{d.okonchanie ? fmt(d.okonchanie) : '—'}</b>
          <span className="ex-pochemu">{d.okonchanie_itog}{d.status ? ' · факт: ' + d.status : ''}</span>
        </div>
      </div>
      {!zakrepleno && prochie.length > 0 && <>
        <div className="ex-zag">Остальные причины — раньше, дату не держат</div>
        <table className="ex-tab"><tbody>
          {prochie.map((k, i) => {
            const r = dni(d.nachalo, k.data);
            return (
              <tr key={i} title={k.poyasnenie || ''}>
                <td>{k.nazvanie}</td>
                <td className="ex-d">{k.data ? fmt(k.data) : '—'}</td>
                <td className="ex-d pw-muted">{r ? 'на ' + r + ' дн раньше' : r === 0 ? 'в тот же день' : ''}</td>
              </tr>);
          })}
        </tbody></table>
      </>}
      {d.ne_deystvuyut.length > 0 && <details className="ex-net">
        <summary>Не держат дату: {d.ne_deystvuyut.length}</summary>
        {d.ne_deystvuyut.map((k, i) => (
          <div key={i}><b>{k.nazvanie}</b>{k.poyasnenie && <span className="pw-muted"> — {k.poyasnenie}</span>}</div>))}
      </details>}
      <div className="bar" style={{ margin: '10px 0 0' }}>
        <button className="sec" title="Штатные и ручные связи задачи" onClick={onLinks}>Предшественники…</button>
      </div>
    </Pop>
  );
}

function DlitPop({ at: a, row, work, korpus, onClose, onSaved, onFail }: {
  at: Anchor; row: Row; work?: Work; korpus: string; onClose: () => void; onSaved: () => void; onFail: (m: string) => void }) {
  const [v, setV] = useState(row.dlit_r != null ? String(row.dlit_r) : '');
  const src = row.obyom != null && work?.trud && work?.chel
    ? 'объём ' + row.obyom + ' ÷ (выработка ' + work.trud + ' × ' + work.chel + ' чел.)' : 'из справочника работ';
  async function save(val: string | null) {
    const r = await call('patch', p('/api/tasks/{tid}', { tid: row.id }), { rev: row.rev, dlit_ruchnaya: val });
    if (!r.ok) { onFail(r.error.message); return; }
    onSaved();
  }
  return (
    <Pop at={a} title={row.rabota + ' · ' + (row.korpus || korpus) + (row.etazh ? ' · эт. ' + row.etazh : '')}
      onClose={onClose} width={380}>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 12px', margin: '10px 0', alignItems: 'center' }}>
        <span>Автоматическая</span>
        <span><b>{row.dlit_avto != null ? row.dlit_avto : '—'}</b> дн{' '}
          <span className="pw-muted">{row.dlit_avto != null ? '(' + src + ')' : '(длительность работе не задана)'}</span></span>
        <span>Ручная</span>
        <input autoFocus style={{ width: 90 }} placeholder="пусто = авто" value={v} onChange={e => setV(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') void save(v.trim() === '' ? null : v.trim()); }} />
      </div>
      <div className="pw-muted" style={{ margin: '0 0 10px' }}>Ручная длительность важнее автоматической.
        Даты пересчитаются после «Пересчитать».</div>
      <div className="bar" style={{ margin: 0 }}>
        <button onClick={() => {
          const t = v.trim();
          if (t !== '' && isNaN(+t.replace(',', '.'))) { onFail('Длительность — число дней'); return; }
          void save(t === '' ? null : t);
        }}>Сохранить</button>
        <button className="sec" onClick={() => void save(null)}>Очистить ручную</button>
      </div>
    </Pop>
  );
}

function LinksPop({ at: a, row, korpusa, works, struct, planKorpus, can, onClose, onSaved, onFail }: {
  at: Anchor; row: Row; korpusa: string[]; works: string[]; struct: Record<string, Floor[]>; planKorpus: string;
  can: boolean; onClose: () => void; onSaved: () => void; onFail: (m: string) => void }) {
  const [d, setD] = useState<LinksGet | null>(null);
  const [f, setF] = useState({ korpus: row.korpus || planKorpus || '', rabota: '', etazh: row.etazh ? String(row.etazh) : '',
    tip: 'ОН', zapas: '', soft: false });
  const reload = useCallback(async () => {
    const r = await call<LinksGet>('get', p('/api/tasks/{tid}/links', { tid: row.id }));
    if (!r.ok) { onFail(r.error.message); onClose(); return; }
    setD(r.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id]);
  useEffect(() => { void reload(); }, [reload]);
  if (!d) return null;
  const dd = (x?: string | null) => (x ? fmt(x) : '—');
  async function toggleOtkl(pred: string, otkl: boolean) {
    const r = await call('post', p('/api/tasks/{tid}/links/otkl', { tid: row.id }), { pred, otkl });
    if (!r.ok) { onFail(r.error.message); return; }
    row.lo = (row.lo || 0) + (otkl ? 1 : -1);
    onSaved(); void reload();
  }
  async function delRuch(id: number) {
    const r = await call('delete', p('/api/task-links/{lid}', { lid: id }));
    if (!r.ok) { onFail(r.error.message); return; }
    row.ml = Math.max(0, (row.ml || 0) - 1);
    onSaved(); void reload();
  }
  async function add() {
    if (!f.rabota.trim() || !f.korpus) { onFail('Укажите работу и корпус предшественника'); return; }
    const r = await call('post', p('/api/tasks/{tid}/links', { tid: row.id }), {
      korpus: f.korpus, rabota: f.rabota.trim(), etazh: f.etazh || null, tip: f.tip, zapas: f.zapas.trim() || null, soft: f.soft });
    if (!r.ok) { onFail(r.error.message); return; }
    row.ml = (row.ml || 0) + 1;
    setF(s => ({ ...s, rabota: '', zapas: '' }));
    onSaved(); void reload();
  }
  return (
    <Pop at={a} title={d.zadacha.rabota + ' · ' + d.zadacha.korpus + (d.zadacha.etazh ? ' · эт. ' + d.zadacha.etazh : ' · корпус целиком')}
      onClose={onClose} width={520}>
      <div className="pw-muted" style={{ margin: '4px 0 8px' }}>Начало задачи — самый поздний из предшественников.
        «Из справочника» — предшественники работы (галочка снята = на этой задаче связь не действует);
        «только у этой задачи» — точечные связи, могут вести на другой этаж или корпус.</div>
      <div style={{ fontWeight: 600, margin: '4px 0' }}>Из справочника (общие для всех этажей)</div>
      {!d.shtatnye.length && <div className="pw-muted">нет</div>}
      {d.shtatnye.map((s, i) => (
        <label key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '2px 0' }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={!s.otkl} disabled={!can}
            title="действует на этой задаче" onChange={e => void toggleOtkl(s.pred, !e.target.checked)} />
          <span style={s.otkl ? { color: '#98a2b3', textDecoration: 'line-through' } : undefined}>
            {s.pred + ' (' + s.tip + (s.zapas ? ', задержка ' + s.zapas : '') + (s.oper ? ', ↑' + s.oper + ' эт' : '')
              + (s.soft ? ', мягкая' : '') + ') → '
              + (s.pred_task ? 'эт. ' + (s.pred_task.etazh || '—') + ': ' + dd(s.pred_task.nachalo) + ' – ' + dd(s.pred_task.okonchanie)
                : 'предшественника на этаже нет')}</span>
        </label>))}
      <div style={{ fontWeight: 600, margin: '8px 0 4px' }}>Только у этой задачи</div>
      {!d.ruchnye.length && <div className="pw-muted">нет</div>}
      {d.ruchnye.map(m => (
        <div key={m.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '2px 0' }}>
          <span style={m.iskl ? { color: '#b42318' } : undefined}>
            {(m.nomer != null ? '№' + m.nomer + ' · ' : '') + m.rabota + ' · ' + m.korpus
              + (m.etazh ? ' · эт. ' + m.etazh : ' · корпус') + ' (' + m.tip + (m.zapas ? ', задержка ' + m.zapas : '')
              + (m.soft ? ', мягкая' : '') + '): ' + dd(m.nachalo) + ' – ' + dd(m.okonchanie) + (m.iskl ? ' — исключена!' : '')}</span>
          {can && <a title="удалить ручную связь" style={{ cursor: 'pointer', color: '#b42318', fontWeight: 700 }}
            onClick={() => void delRuch(m.id)}>×</a>}
        </div>))}
      {can && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 10px', margin: '10px 0 6px', alignItems: 'center' }}>
          <span>Добавить ручную</span><span className="pw-muted">корпус · работа · этаж · тип · запас</span>
          <span></span>
          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={f.korpus} onChange={e => setF(s => ({ ...s, korpus: e.target.value, etazh: '' }))}>
              <option value=""></option>{korpusa.map(k => <option key={k} value={k}>{k}</option>)}</select>
            <PoleSpiska value={f.rabota} opts={works} placeholder="работа" style={{ width: 170 }}
              onChange={v => setF(s => ({ ...s, rabota: v }))} onEnter={() => void add()} />
            <select value={f.etazh} onChange={e => setF(s => ({ ...s, etazh: e.target.value }))}>
              <option value="">— корпус целиком —</option>
              {(struct[f.korpus] || []).map(fl => <option key={fl.id} value={String(fl.etazh)}>{fl.etazh}</option>)}</select>
            <select value={f.tip} onChange={e => setF(s => ({ ...s, tip: e.target.value }))}>
              <option value="ОН">оконч. → нач.</option><option value="НН">нач. → нач.</option></select>
            <input placeholder="задержка" style={{ width: 70 }} value={f.zapas} onChange={e => setF(s => ({ ...s, zapas: e.target.value }))} />
            <label className="pw-muted" title="можно идти вперёд, если предшественник отстаёт">
              <input type="checkbox" style={{ width: 'auto' }} checked={f.soft}
                onChange={e => setF(s => ({ ...s, soft: e.target.checked }))} /> мягкая</label>
            <button onClick={() => void add()}>Добавить</button>
          </span>
        </div>
        <div className="pw-muted">после правок — «Пересчитать»</div>
      </>}
    </Pop>
  );
}

function BulkLinksPop({ at: a, pid, ids, korpusa, works, struct, onClose, onDone, onFail }: {
  at: Anchor; pid: number; ids: number[]; korpusa: string[]; works: string[]; struct: Record<string, Floor[]>;
  onClose: () => void; onDone: (m: string) => void; onFail: (m: string) => void }) {
  const [v, setV] = useState({ rabota: '', rezhim: 'etazh', sdvig: '', korpus: korpusa[0] || '', etazh: '',
    tip: 'ОН', zapas: '', soft: false });
  async function go() {
    if (!v.rabota.trim()) { onFail('Укажите работу-предшественника'); return; }
    const body: Record<string, unknown> = { ids, rabota: v.rabota.trim(), rezhim: v.rezhim,
      sdvig: v.sdvig.trim() || 0, tip: v.tip, zapas: v.zapas.trim() || null, soft: v.soft };
    if (v.rezhim === 'zadacha') {
      const r0 = await call<Tasks>('get', p('/api/projects/{pid}/tasks', { pid })
        + '?korpus=' + encodeURIComponent(v.korpus) + '&rabota=' + encodeURIComponent(v.rabota.trim()) + '&limit=20000');
      const et = v.etazh || null;
      const cand = r0.ok ? r0.data.rows.find(x => (x.etazh || null) === et) : null;
      if (!cand) { onFail('Задача-предшественник не найдена'); return; }
      body.pred_task_id = cand.id;
    }
    const r = await call<{ sozdano: number; propuscheno: number }>('post',
      p('/api/projects/{pid}/task-links/bulk', { pid }), body);
    if (!r.ok) { onFail(r.error.message); return; }
    onDone('Ручных связей создано: ' + r.data.sozdano + ', пропущено: ' + r.data.propuscheno
      + '. Нажмите «Пересчитать», чтобы обновить даты.');
  }
  return (
    <Pop at={a} title={'Предшественник для ' + ids.length + ' задач'} onClose={onClose} width={460}>
      <div className="pw-muted" style={{ margin: '4px 0 8px' }}>Каждой выбранной задаче добавится ручная связь с указанной
        работой: на том же этаже, на этаже со сдвигом или с одной конкретной задачей.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 10px', alignItems: 'center' }}>
        <span>Работа-предшественник</span>
        <PoleSpiska value={v.rabota} opts={works} placeholder="работа"
          onChange={x => setV(s => ({ ...s, rabota: x }))} />
        <span>Режим</span>
        <select value={v.rezhim} onChange={e => setV(s => ({ ...s, rezhim: e.target.value }))}>
          <option value="etazh">тот же этаж</option><option value="sdvig">этаж со сдвигом</option>
          <option value="zadacha">конкретная задача</option></select>
        {v.rezhim === 'sdvig' && <><span>Сдвиг этажей</span>
          <input placeholder="напр. 2 или -1" style={{ width: 100 }} value={v.sdvig}
            onChange={e => setV(s => ({ ...s, sdvig: e.target.value }))} /></>}
        {v.rezhim === 'zadacha' && <><span>Задача</span>
          <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <select value={v.korpus} onChange={e => setV(s => ({ ...s, korpus: e.target.value, etazh: '' }))}>
              {korpusa.map(k => <option key={k} value={k}>{k}</option>)}</select>
            <select value={v.etazh} onChange={e => setV(s => ({ ...s, etazh: e.target.value }))}>
              <option value="">— корпус целиком —</option>
              {(struct[v.korpus] || []).map(fl => <option key={fl.id} value={String(fl.etazh)}>{fl.etazh}</option>)}</select>
          </span></>}
        <span>Тип</span>
        <select value={v.tip} onChange={e => setV(s => ({ ...s, tip: e.target.value }))}>
          <option value="ОН">окончание → начало (ОН)</option><option value="НН">начало → начало (НН)</option></select>
        <span>Задержка, дн</span>
        <input style={{ width: 70 }} value={v.zapas} onChange={e => setV(s => ({ ...s, zapas: e.target.value }))} />
        <span>Мягкая</span>
        <label className="pw-muted"><input type="checkbox" style={{ width: 'auto' }} checked={v.soft}
          onChange={e => setV(s => ({ ...s, soft: e.target.checked }))} /> можно идти вперёд, если предшественник отстаёт</label>
      </div>
      <div className="bar" style={{ margin: '10px 0 0' }}><button onClick={() => void go()}>Назначить</button></div>
    </Pop>
  );
}

/** id шаблона «Базовый» проекта — для связи, заданной именем работы */
async function baseTpl(pid: number): Promise<number | null> {
  const r = await call<{ id: number; name: string }[]>('get', '/api/link-templates?project=' + pid);
  if (!r.ok) return null;
  const t = r.data.find(x => x.name.toLowerCase() === 'базовый');
  if (t) return t.id;
  const c = await call<{ id: number }>('post', '/api/link-templates', { name: 'Базовый', project: pid });
  return c.ok ? c.data.id : null;
}

/** Строка «＋ новая работа» внизу «Модели ГПР» (владелец 2026-10-15, «хочу вносить работы прямо
    в графике»; до того работу можно было завести только в «Иерархии», «Ручном вводе» или на
    экране «Работы»). Название + Enter → работа с признаками образца (последняя работа в таблице:
    раскатка, порядок этажей, дни, этап, контур, календарь), задачи по раскатке, пересчёт дат,
    курсор снова в строке — следующую можно вводить сразу. */
let nwFokus = false;   // eslint-disable-line @typescript-eslint/no-unused-vars
// вернуть курсор в строку после перечитывания таблицы (строка пересоздаётся)
export function AddWorkRow({ ctx, obrazec, estSek, onAdded, onFail }: {
  ctx: ShellCtx; obrazec: Work | null; estSek: boolean;
  onAdded: (m: string, aktual?: string | null) => void; onFail: (m: string) => void }) {
  const [v, setV] = useState('');
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  // после Enter таблица перечитывается и строка монтируется заново — фокус ставим на новой
  useEffect(() => { if (nwFokus && ref.current && !busy) { nwFokus = false; ref.current.focus(); } });
  async function save() {
    const name = v.trim();
    if (!name || busy) return;
    setBusy(true);
    const body: Record<string, unknown> = { name, raskatka: obrazec?.raskatka || 'этажи' };
    if (obrazec) {
      body.posle_work_id = obrazec.id;   // в иерархии — сразу за образцом, в его раздел
      if (obrazec.etap) body.etap = obrazec.etap;
      if (obrazec.logika) body.logika_montazha = obrazec.logika;
      if (obrazec.dlit != null) body.dlitelnost = obrazec.dlit;
      if (obrazec.kontur) body.kontur = obrazec.kontur;
      if (obrazec.kalendar) body.kalendar = obrazec.kalendar;
    }
    const r = await call<{ id: number; sozdano_zadach: number; raskatka: string; razdel: string | null }>('post',
      p('/api/projects/{pid}/works', { pid: ctx.pid }), body);
    if (!r.ok) { setBusy(false); onFail(r.error.message); ref.current?.focus(); return; }
    // даты — сразу, без кнопки «Пересчитать»: работа должна появиться на Ганте с полосой
    const rc = await call<{ aktualizirovano: string | null }>('post', p('/api/projects/{pid}/recalc', { pid: ctx.pid }), {});
    setBusy(false); setV(''); nwFokus = true;
    const kak = (r.data.razdel ? ', этап «' + r.data.razdel + '»' : '') + (obrazec ? ', признаки как у «' + obrazec.name + '»' : '');
    if (rc.ok) onAdded('«' + name + '»: задач ' + r.data.sozdano_zadach + ' (' + r.data.raskatka + kak + '), даты пересчитаны.', rc.data.aktualizirovano);
    else onAdded('«' + name + '»: задач ' + r.data.sozdano_zadach + kak + '. Пересчёт не прошёл: ' + rc.error.message);
    setTimeout(() => ref.current?.focus(), 0);
  }
  return (
    <tr id="nwRow" title={obrazec ? 'Новая работа получит раскатку, порядок этажей, дни и этап как у «' + obrazec.name + '»; потом их можно поправить в карточке работы' : 'Первая работа проекта: раскатка «по типовым этажам»'}>
      <td className="num fx-n ra-plus">＋</td>
      <td className="fx-k"></td>
      <td className="fx-e"></td>
      {estSek && <td className="col-zahv"></td>}
      <td className="fx-w">
        <input ref={ref} className="nw-inp" value={v} disabled={busy} id="nwInp"
          placeholder={busy ? 'Создаю задачи и считаю даты…' : 'новая работа — впишите название и нажмите Enter'}
          onChange={e => setV(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void save(); } if (e.key === 'Escape') setV(''); }} />
      </td>
      <td colSpan={30} className="nw-note">
        {obrazec ? <span className="muted">как «{obrazec.name}»: {obrazec.raskatka}{obrazec.logika ? ' · ' + obrazec.logika : ''}{obrazec.dlit != null ? ' · ' + obrazec.dlit + ' дн' : ''}</span> : null}
      </td>
    </tr>
  );
}

/** Строка «＋» внизу «Ручного планирования»: работа, длительность, предшественник.
    Enter сохраняет; в поле работы можно вставить список из буфера — строками
    «работа [tab] дней [tab] предшественник» (2026-09-08). */
function AddTaskRow({ ctx, meta, struct, korpusa, glubina, vvod, fokus, vyshe, onSozdana, onTab, onShiftTab, onAdded, onFail }: {
  ctx: ShellCtx; meta: Record<string, Work>; struct: Record<string, Floor[]>; korpusa: string[];
  /** задача над строкой ввода: Ctrl+D в поле берёт её значение (владелец 2026-10-27: «как Ctrl+D») */
  vyshe: Row | null;
  /** место в дереве ручного планирования (#126): отступ, группа и строка, после которой встаёт новая задача */
  glubina: number; vvod: Vvod; fokus: number;
  onSozdana: (id: number) => void; onTab: (imya: string) => Promise<boolean>; onShiftTab: () => void;
  onAdded: (m: string) => void; onFail: (m: string) => void }) {
  // корпус по умолчанию пустой — задача на объект (#126: ручное планирование почти ничем не ограничено)
  const [v, setV] = useState({ work: '', dlit: '', pred: '', korpus: '', etazh: '', mesto: '' });
  const box = useRef<HTMLTableRowElement>(null);
  // строку ввода поставили на новое место (Enter у строки, Tab / Shift+Tab) — курсор в название
  useEffect(() => {
    if (fokus) window.setTimeout(() => box.current?.querySelector<HTMLInputElement>('td.fx-w input')?.focus(), 0);
  }, [fokus]);
  const manualWorks = Object.values(meta).filter(w => w.raskatka === 'вручную'
    || kontury(w.kontur).includes('Ручной')).map(w => w.name).sort();
  const allWorks = Object.keys(meta).sort();

  /** создать одну задачу после строки posle; возвращает № и id созданной задачи (для цепочки) */
  async function sozdat(name: string, dlitRaw: string, pred: string, korp: string, et: string, mesto: string,
    posle: Vvod['posle']) {
    if (!name) { onFail('Укажите работу'); return null; }
    if (dlitRaw !== '' && isNaN(+dlitRaw.replace(',', '.'))) { onFail('Длительность — число дней'); return null; }
    if (pred && pred === name) { onFail('Работа не может быть предшественником самой себе'); return null; }
    let wid = meta[name]?.id;
    let created = '';
    if (!wid) {
      const rc = await call<{ id: number }>('post', p('/api/projects/{pid}/works', { pid: ctx.pid }),
        { name, kontur: 'Ручной', raskatka: 'вручную' });
      if (!rc.ok) { onFail(rc.error.message); return null; }
      wid = rc.data.id;
      created = ' (работа создана в справочнике с контуром «Ручной»)';
    }
    const r = await call<{ id: number; nomer?: number | null }>('post',
      p('/api/projects/{pid}/tasks/single', { pid: ctx.pid }),
      { work_id: wid, korpus: korp || null, etazh: et || null, zahvatka: mesto || null,
        dlit_ruchnaya: dlitRaw === '' ? null : dlitRaw, ruch_gruppa_id: vvod.parent, posle });
    if (!r.ok) { onFail(r.error.message); return null; }
    let linkNote = '';
    if (pred && /^\s*\d+/.test(pred)) {
      const rl = await call('patch', p('/api/tasks/{tid}', { tid: r.data.id }), { rev: 1, pred_ruch: pred });
      if (rl.ok) linkNote = ' ← №' + pred; else onFail('Предшественник не назначен: ' + rl.error.message);
    } else if (pred) {
      const tid = await baseTpl(ctx.pid);
      if (tid) {
        const rl = await call('post', p('/api/link-templates/{tid}/rows', { tid }), { work: name, pred });
        if (rl.ok || rl.error.code === 'exists' || /уже есть/i.test(rl.error.message)) linkNote = ' ← ' + pred;
        else onFail('Связь не создана: ' + rl.error.message);
      }
    }
    const nomer = r.data.nomer != null ? String(r.data.nomer) : name;
    onSozdana(r.data.id);
    onAdded('Задача добавлена: ' + name + (korp ? ' · ' + korp : '') + (et ? ' · эт. ' + et : '') + (mesto ? ' · ' + mesto : '')
      + (dlitRaw ? ' · ' + dlitRaw + ' дн' : '') + linkNote + created + '. Даты — после «Пересчитать».');
    return { nomer, id: r.data.id };
  }

  async function save() {
    const z = await sozdat(v.work.trim(), v.dlit.trim(), v.pred.trim(), v.korpus, v.etazh.trim(), v.mesto.trim(), vvod.posle);
    if (z === null) return;
    // корпус, этаж и место остаются — следующая работа обычно там же (ярус, захватка)
    setV(s => ({ ...s, work: '', dlit: '', pred: '' }));
  }

  /** вставка списка из буфера: строки «работа [tab] дней [tab] предшественник» */
  async function vstavit(e: React.ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    const lines = text.split(/\r?\n/).map(x => x.trim()).filter(Boolean);
    if (lines.length < 2 && !text.includes('\t')) return;      // обычная вставка слова
    e.preventDefault();
    let pred = v.pred.trim();
    let n = 0;
    let posle = vvod.posle;
    for (const line of lines) {
      const [name, dlit, pr] = line.split('\t');
      const z = await sozdat((name || '').trim(), (dlit || '').trim(),
        (pr || '').trim() || pred, v.korpus, v.etazh.trim(), v.mesto.trim(), posle);
      if (z === null) break;
      posle = { tip: 'task', id: z.id };
      n++;
      pred = '';
    }
    setV(s => ({ ...s, work: '', dlit: '', pred: '' }));
    if (n > 1) onAdded('Добавлено задач: ' + n + '. Даты — после «Пересчитать».');
  }

  /** Ctrl+D — «заполнить вниз», как в Excel: значение этого поля у задачи над строкой ввода (и при русской раскладке) */
  const sverhu = (pole: 'work' | 'etazh' | 'mesto' | 'dlit' | 'korpus') => (e: React.KeyboardEvent) => {
    if (!((e.ctrlKey || e.metaKey) && e.code === 'KeyD')) return false;
    e.preventDefault();
    if (!vyshe) return true;
    const zn = { work: vyshe.rabota || '', etazh: vyshe.etazh || '', mesto: vyshe.zahvatka || '',
      dlit: vyshe.dlit_r != null ? String(vyshe.dlit_r) : vyshe.dlit != null ? String(vyshe.dlit) : '',
      korpus: vyshe.korpus && vyshe.korpus !== 'Объект' ? vyshe.korpus : '' }[pole];
    setV(s0 => ({ ...s0, [pole]: zn }));
    return true;
  };
  return (
    <tr id="raRow" ref={box} style={{ height: 25 }}>
      <td className="fx-n ra-plus">＋</td>
      <td className="fx-k">
        <select value={v.korpus} title="Корпус задачи; пусто — задача на объект. Ctrl+D — как у задачи выше" style={{ width: '100%' }}
          onChange={e => setV(s => ({ ...s, korpus: e.target.value }))} onKeyDown={sverhu('korpus')}>
          <option value="">—</option>{korpusa.map(k => <option key={k} value={k}>{k}</option>)}</select>
      </td>
      <td className="fx-e">
        <PoleSpiska value={v.etazh} opts={(struct[v.korpus] || []).map(fl => String(fl.etazh))} placeholder="эт."
          style={{ width: '100%' }} onChange={x => setV(s => ({ ...s, etazh: x }))} onEnter={() => void save()}
          onKeyDown={sverhu('etazh')}
          title="Этаж или ярус — любой текст («ярус 2», «−6,0») или этаж корпуса из списка; пусто — без этажа. Ctrl+D — как у задачи выше" />
      </td>
      <td className="col-zahv">
        <input value={v.mesto} placeholder="место" style={{ width: '100%' }} title="Место: захватка, участок — любой текст. Ctrl+D — как у задачи выше"
          onChange={e => setV(s => ({ ...s, mesto: e.target.value }))}
          onKeyDown={e => { if (sverhu('mesto')(e)) return; if (e.key === 'Enter') { e.preventDefault(); void save(); } }} />
      </td>
      <td className="fx-w" style={{ paddingLeft: 4 + glubina * 14 }}>
        <PoleSpiska value={v.work} opts={manualWorks} placeholder="работа или группа: Tab — сделать группой"
          style={{ width: '100%' }} onChange={x => setV(s => ({ ...s, work: x }))}
          onEnter={() => void save()} onPaste={e => void vstavit(e)}
          onKeyDown={e => {
            if (sverhu('work')(e)) return;
            if (e.key !== 'Tab') return;
            e.preventDefault();
            if (e.shiftKey) { onShiftTab(); return; }
            const imya = v.work.trim();
            void onTab(imya).then(okk => { if (okk && imya) setV(s => ({ ...s, work: '' })); });
          }}
          title="Существующая ручная работа — из списка; новое название создаст работу в справочнике с контуром «Ручной». Enter — сохранить задачу; Tab — набранное станет группой (пусто — вложить в строку выше), Shift+Tab — на уровень выше; Ctrl+D — название работы задачи выше; можно вставить список строками из буфера" />
      </td>
      <td className="col-itog col-b"></td>
      <td className="col-itog"></td>
      <td className="col-itog col-e">
        <input placeholder="дн" className="num" style={{ width: '100%' }} title="Длительность, дней. Ctrl+D — как у задачи выше" value={v.dlit}
          onChange={e => setV(s => ({ ...s, dlit: e.target.value }))}
          onKeyDown={e => { if (sverhu('dlit')(e)) return; if (e.key === 'Enter') { e.preventDefault(); void save(); } }} />
      </td>
      <td className="col-ruch-pred ra-cell">
        {/* как ячейка Excel / Project (владелец 2026-10-27): без подсказки в строке — она под таблицей */}
        <PoleSpiska value={v.pred} opts={allWorks} placeholder="№ задачи или работа"
          style={{ width: '100%' }} onChange={x => setV(s => ({ ...s, pred: x }))}
          onEnter={() => void save()}
          title="Предшественник (окончание → начало): номер задачи ставит ручную связь, название работы — связь в схеме «Базовый». Enter — сохранить строку" />
      </td>
      <td colSpan={99}></td>
    </tr>
  );
}


/** Замечание к задаче: `gruppa` — «ошибка» или «конфликт», `chto` — названия
 *  сработавших проверок (показываем в подсказке строки). */
type Problema = { tid: number; gruppa: string; chto: string[] };
export type SvodkaOshibok = { vsego: number; vidy: { chto: string; gruppa: string; n: number }[] };

/** Больше стрелок экран не читает, а рисование заметно тормозит (2026-09-10). */
const PREDEL_STRELOK = 400;

/** Пара для стрелки: `ot` — задача-предшественник, `do` — задача, `vid` —
 *  svyaz (связь справочника) | ruch (ручной предшественник) | resurs (бригада). */
type Para = { ot: number; do: number; vid: string };

/** Слой стрелок над Гантом (2026-09-10).
 *
 *  Координаты не вычисляем по датам, а берём у самих полос в разметке: так
 *  стрелка не разъезжается с полосой ни при перетаскивании, ни при прокрутке.
 *  Рисуем только те пары, у которых обе задачи сейчас на экране, — таблица
 *  виртуализована, за окном строк просто нет. `risuj` — любое меняющееся
 *  число (прокрутка, число строк): по нему пересчитываем.
 */
function SvyaziSloj({ box, pary, rabotaZadachi, risuj }: {
  box: React.RefObject<HTMLDivElement>; pary: Para[];
  rabotaZadachi: Map<number, string>; risuj: number }) {
  const [linii, setLinii] = useState<{ d: string; vid: string; key: string }[]>([]);
  const [razmer, setRazmer] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const el = box.current;
    if (!el || !pary.length) { setLinii([]); return; }
    const kontejner = el.getBoundingClientRect();
    type Tochka = { x1: number; x2: number; y: number; h: number };
    const mesto = (bar: Element): Tochka => {
      const r = bar.getBoundingClientRect();
      return {
        x1: r.left - kontejner.left + el.scrollLeft,
        x2: r.right - kontejner.left + el.scrollLeft,
        y: r.top - kontejner.top + el.scrollTop + r.height / 2,
        h: r.height,
      };
    };
    const koord: Record<number, Tochka> = {};
    const koordGrp: Record<string, Tochka> = {};
    el.querySelectorAll('tr[data-tid]').forEach(tr => {
      const bar = tr.querySelector('.viz-bar');
      if (bar) koord[Number((tr as HTMLElement).dataset.tid)] = mesto(bar);
    });
    // свёрнутая работа: вместо задач видна одна суммарная полоса — ведём к ней
    el.querySelectorAll('tr[data-grp]').forEach(tr => {
      const bar = tr.querySelector('.viz-bar');
      if (bar) koordGrp[String((tr as HTMLElement).dataset.grp)] = mesto(bar);
    });
    const tochka = (tid: number): Tochka | null => {
      if (koord[tid]) return koord[tid];
      const rab = rabotaZadachi.get(tid);
      return (rab && koordGrp[rab]) || null;
    };
    const out: { d: string; vid: string; key: string }[] = [];
    const bylo = new Set<string>();
    for (const pr of pary) {
      if (out.length >= PREDEL_STRELOK) break;
      const a = tochka(pr.ot), b = tochka(pr.do);
      if (!a || !b || a === b) continue;             // не видно или это одна и та же полоса
      // после подъёма к группам одна и та же стрелка может получиться много раз
      const klyuch = Math.round(a.x2) + ':' + Math.round(a.y) + '→'
        + Math.round(b.x1) + ':' + Math.round(b.y) + ':' + pr.vid;
      if (bylo.has(klyuch)) continue;
      bylo.add(klyuch);
      // ломаная «от конца предшественника к началу последователя», как в Ганте:
      // немного вправо, вертикаль, подход к началу полосы стрелкой
      const x0 = a.x2, y0 = a.y, x1 = b.x1, y1 = b.y;
      const otstup = 8;
      // если последователь начинается не правее конца предшественника, стрелка
      // делает горизонтальный шаг назад — сразу под (над) полосой предшественника,
      // в зазоре между строками, а не посередине пути: середина попадала на чужую
      // полосу (суммарную), и казалось, что связь ведёт к ней (замечание 2026-09-13)
      const shag = y1 > y0 ? y0 + a.h / 2 + 4 : y0 - a.h / 2 - 4;
      const d = x1 >= x0 + otstup * 2
        ? `M ${x0} ${y0} H ${x1 - otstup} V ${y1} H ${x1}`
        : `M ${x0} ${y0} H ${x0 + otstup} V ${shag} H ${x1 - otstup} V ${y1} H ${x1}`;
      out.push({ d, vid: pr.vid, key: klyuch });
    }
    setLinii(out);
    setRazmer({ w: el.scrollWidth, h: el.scrollHeight });
  }, [box, pary, rabotaZadachi, risuj]);

  if (!linii.length) return null;
  return (
    <svg className="viz-svyazi" width={razmer.w} height={razmer.h}>
      <defs>
        <marker id="strelka" viewBox="0 0 8 8" refX="7" refY="4" markerWidth="6" markerHeight="6"
          orient="auto-start-reverse">
          <path d="M 0 1 L 7 4 L 0 7 z" />
        </marker>
      </defs>
      {linii.map(l => (
        <path key={l.key} d={l.d} className={'viz-svyaz viz-svyaz-' + l.vid}
          markerEnd="url(#strelka)" />
      ))}
    </svg>
  );
}
